export interface StoryBeat {
  title: string;
  prompt: string;
  videoUrl: string;
  videoPrompt: string;
  videoDurationSeconds: 5;
  videoHasAudio: false;
}

export interface StoryStep extends StoryBeat {
  index: number;
  isExtension: boolean;
}

export interface StoryDirectorPlan {
  lockReason: string;
  narrations: StoryNarrationCue[];
  visualBeats: StoryVisualBeat[];
  response: {
    delayMs: number;
    instructions: string;
    scriptedText?: string;
  };
  continuation?: {
    delayMs: number;
    lockReason: string;
    narration?: {
      title: string;
      text: string;
      durationMs: number;
    };
    instructions: string;
    scriptedText?: string;
  };
  transition?: {
    title: string;
    detail: string;
  };
  imageBeats?: StoryImageBeat[];
  inputRelease?: StoryInputRelease;
  timeline?: StoryDirectorTimelineEvent[];
  visualTarget?: StoryVisualTarget;
  choicePrompt: string;
}

export type StoryVisualFocus = "wide" | "medium" | "detail";
export type StoryVisualMood = "observe" | "tense" | "reveal";
export type StoryImageReferenceMode = "character" | "scene" | "both";
export type StoryInputRelease = "after_first_line" | "after_sequence" | "interruptible";
export type StoryNarrationPresentation = "caption" | "ambient";

export interface StoryNarrationCue {
  title: string;
  text: string;
  durationMs: number;
  presentation?: StoryNarrationPresentation;
}

export interface StoryImageBeat {
  type: "image";
  delayMs: number;
  title: string;
  prompt: string;
  referenceMode: StoryImageReferenceMode;
  reason: string;
}

export interface StoryVisualTarget {
  title: string;
  prompt: string;
  referenceMode: StoryImageReferenceMode;
  sourceSummary: string;
}

export type StoryVisualBeat =
  {
    type: "camera";
    delayMs: number;
    label: string;
    detail: string;
    focus: StoryVisualFocus;
    mood: StoryVisualMood;
  };

export type StoryDirectorTimelineEvent =
  | {
      type: "feedback";
      delayMs: number;
      label: string;
      text: string;
      mood: "heard" | "observing" | "handoff";
    }
  | {
      type: "camera";
      delayMs: number;
      beat: Extract<StoryVisualBeat, { type: "camera" }>;
    }
  | {
      type: "narration";
      delayMs: number;
      cue: StoryNarrationCue & { presentation: StoryNarrationPresentation };
    }
  | {
      type: "response";
      delayMs: number;
      response: StoryDirectorPlan["response"];
    }
  | {
      type: "image";
      delayMs: number;
      beat: StoryImageBeat;
    };

export interface StoryDirectorPlanInput {
  advanced: boolean;
  beatIndex: number;
  playerText: string;
  priorAssistantText?: string;
  title: string;
  turnsInBeat: number;
  turnsRequired: number;
}

export interface UnifiedTurnPlan extends Omit<StoryDirectorPlan, "narrations" | "imageBeats" | "inputRelease"> {
  narrations: Array<StoryNarrationCue & { presentation: StoryNarrationPresentation }>;
  imageBeats: StoryImageBeat[];
  inputRelease: StoryInputRelease;
  timeline: StoryDirectorTimelineEvent[];
  visualTarget: StoryVisualTarget;
}

export interface ConversationImagePromptInput {
  assistantText: string;
  playerText: string;
  title: string;
}

export interface VisualAwareChoicePromptInput {
  basePrompt: string;
  storyTitle: string;
  visualTitle?: string;
}

interface StoryAdvanceOptions {
  maxForward?: number;
}

export const STORY_EXTENSION_TITLE = "延展线索";
export const STORY_TURNS_PER_BEAT = 3;

export const STORY_EXTENSION_PROMPT =
  "After all listed supermarket clues have surfaced, Lin Xia stays in the same grounded late-night supermarket mystery and follows the player's newest choice into a new connected clue, preserving continuity instead of ending the call";

const VIDEO_STYLE =
  "silent 5-second anime visual novel reward clip, no dialogue, no voice, no music, no subtitles, no readable text, no watermark, same adult Chinese woman Lin Xia with rose-pink shoulder-length bob hair and straight bangs, black cropped leather jacket, white outfit, black choker, dark stockings, white ankle boots, cinematic handheld camera, grounded late-night Chinese supermarket realism";

const LIN_XIA_IMAGE_STYLE =
  "same adult Chinese woman Lin Xia, rose-pink shoulder-length bob hair with straight bangs, black cropped leather jacket, white outfit, black choker, dark stockings, white ankle boots, phone in hand, anime visual novel key art";

const LIN_XIA_DIALOGUE_RULE =
  "只说电话里会说出口的话，1-3个短句，总字数70个中文字符以内；每句尽量短。只称呼玩家为“你”，不要用“您”。只说现场可见、可听、可触摸的异常，不展开推理链，不写心理或动作描写，不评价自己是否紧张、害怕或冷静，绝对不要说“我紧张”“我害怕”“我冷静”。玩家用“如果她开始紧张”这类条件句时，忽略情绪判断，直接执行后半句安全动作并汇报现场。不要用推测式解读词，不用省略号或破折号截断半句话，也不要以未完成短语结尾。不要用“看起来不太对劲”“有点问题”“不太正常”“怪怪的”这类泛化判断收尾；如果要表达异常，必须说出具体物体、位置和感官细节。玩家说某个线索是障眼法、先别看或不用管时，本轮不要把它当重点，也不要反问是否回去看它。不要固定句式，不要重复上一句关键词组合；同一回复内部不得重复同一个动作、位置或观察结果，不能先说一遍再换词复述一遍；如果玩家同时要求动作和确认多个细节，只说一次动作，再把结果合并成一句。不要说“我再看看其他地方”“我继续看其他地方”这类机械尾句。";

function makeStoryBeat(title: string, slug: string, prompt: string, videoAction: string): StoryBeat {
  return {
    title,
    prompt,
    videoUrl: `/story-videos/${slug}.mp4`,
    videoPrompt: `${VIDEO_STYLE}. ${videoAction}. ${prompt}.`,
    videoDurationSeconds: 5,
    videoHasAudio: false
  };
}

export const STORY_BEATS: StoryBeat[] = [
  makeStoryBeat(
    "超市后门吸烟点",
    "01-back-door-break-spot",
    "Lin Xia sits alone on yellow plastic produce crates at the staff break spot behind a late-night supermarket, gray concrete wall, stacked cardboard boxes, metal shelves, freezer units, thin white vapor in the cold air, dim fluorescent light, quiet after-hours mood",
    "Lin Xia lifts her phone, looks toward the service yard, cold vapor drifting past yellow crates as the camera slowly pushes in"
  ),
  makeStoryBeat(
    "未结账的小票",
    "02-unpaid-receipt",
    "Lin Xia alone on yellow supermarket crates, examining a crumpled receipt under cold fluorescent light, gray concrete wall and cardboard boxes behind her, thin white vapor drifting in the cold air, tense visual novel atmosphere",
    "Lin Xia unfolds a crumpled receipt under flickering fluorescent light, her eyes narrowing as the paper trembles slightly in the cold air"
  ),
  makeStoryBeat(
    "冷库门缝",
    "03-cold-storage-gap",
    "Lin Xia alone near the half-open cold storage door behind the supermarket, blue-white freezer light cutting through cold white vapor, yellow crates and metal storage shelves nearby, no other people",
    "The camera follows Lin Xia as she stops before a half-open cold storage door, blue-white freezer light spilling across her boots"
  ),
  makeStoryBeat(
    "监控死角",
    "04-security-blind-spot",
    "Lin Xia alone below an old security camera in the supermarket back corridor, leaning against a gray concrete wall beside yellow produce crates, suspicious but restrained expression, cinematic still",
    "Lin Xia glances up at an old security camera, then steps just outside its view while shadows from stacked crates cut across the wall"
  ),
  makeStoryBeat(
    "凌晨三点的货梯",
    "05-freight-elevator-3am",
    "Lin Xia alone in front of a freight elevator behind the supermarket at 3 AM, black cropped leather jacket and white outfit, phone screen glow on her face, stacked boxes and yellow crates, industrial shadows",
    "The freight elevator indicator flickers at 3 AM while Lin Xia stands still, phone glow on her face, waiting for the doors to move"
  ),
  makeStoryBeat(
    "地漏旁的钥匙",
    "06-floor-drain-key",
    "Lin Xia alone crouching beside yellow crates and a floor drain, finding a small key near dark scuff marks on the floor, supermarket loading area, wet concrete reflections, tense close medium shot",
    "Lin Xia crouches beside a floor drain and reaches toward a small key on wet concrete, the camera dropping to a tense close shot"
  ),
  makeStoryBeat(
    "货架后的呼吸声",
    "07-breath-behind-shelves",
    "Lin Xia alone inside the dim supermarket stockroom, metal shelves and cardboard boxes around her, listening toward the back entrance staff break spot, grounded suspense",
    "Lin Xia freezes between metal shelves, slowly turning her head toward a sound behind the boxes while the aisle light pulses once"
  ),
  makeStoryBeat(
    "后门外的来电",
    "08-call-outside-back-door",
    "Lin Xia alone under the supermarket back door awning, phone against her ear, mist and rain in the concrete service yard, yellow crates behind her, determined expression",
    "Lin Xia steps under the back door awning with the phone to her ear, rain mist crossing the service yard as she decides to keep the call open"
  )
];

export function getStoryBeatIndex(title: string) {
  return STORY_BEATS.findIndex((beat) => beat.title === title);
}

export function getStoryBeatPrompt(title: string) {
  if (title === STORY_EXTENSION_TITLE) return STORY_EXTENSION_PROMPT;
  return STORY_BEATS.find((beat) => beat.title === title)?.prompt ?? title;
}

export function buildConversationImagePrompt(input: ConversationImagePromptInput) {
  const groundedPlayerText = normalizeSpeechCueText(getGroundedPlayerVisualText(input.assistantText, input.playerText));
  const assistantVisualText = removeUnexecutedBranchTerms(input.assistantText);
  const suppressedConditionalHint = Boolean(input.playerText.trim() && !groundedPlayerText);
  const absentClues = mergeSets(inferAbsentVisualClues(assistantVisualText), inferAbsentVisualClues(groundedPlayerText));
  const deprioritizedClues = inferDeprioritizedVisualClues(groundedPlayerText);
  const visualIntent = inferDialogueVisualIntent({ ...input, assistantText: assistantVisualText, playerText: groundedPlayerText });
  const linXiaSource = summarizeVisualSource(assistantVisualText, absentClues, deprioritizedClues);
  const playerSource = summarizeGroundedPlayerVisualHint(groundedPlayerText, absentClues, deprioritizedClues);
  const assistantCueText = removeExitedLocationTerms(removeDeprioritizedCueTerms(removeAvoidedCueTerms(assistantVisualText), deprioritizedClues));
  const playerCueText = removeExitedLocationTerms(removeDeprioritizedCueTerms(removeAvoidedCueTerms(groundedPlayerText), deprioritizedClues));
  const latestText = `${assistantCueText} ${playerCueText}`;
  const latestLocationCue = inferLatestLocationCue(latestText, input.title);
  const hasLatestVisualFacts = Boolean(linXiaSource || playerSource);
  const latestMentionsPaperClue = mentionsPresentPaperClue(assistantCueText) || mentionsPresentPaperClue(playerCueText);
  const dialogueDirectives = [
    "highest priority: depict the latest physical facts Lin Xia just said on the phone, not a prewritten storyboard",
    linXiaSource
      ? `latest Lin Xia dialogue source, use as visual facts and do not render as text: ${linXiaSource}`
      : `latest Lin Xia dialogue source is vague, infer from current story beat: ${getStoryBeatImageCue(input.title)}`,
    playerSource
      ? `grounded player hint that may influence camera or clue choice: ${playerSource}`
      : suppressedConditionalHint
        ? "player hint was conditional and Lin Xia did not confirm or execute it; do not depict that player-suggested branch as a visual fact"
      : "ignore greetings, jokes, impossible requests, and off-location player ideas unless Lin Xia grounded them in the supermarket scene",
    `dialogue visual intent: ${visualIntent}`,
    hasLatestVisualFacts
      ? `current story chapter is continuity only: keep Lin Xia inside the same grounded late-night supermarket investigation, ${latestLocationCue}`
      : `fallback story setting if latest dialogue has no usable physical detail: ${getStoryBeatPrompt(input.title)}`,
    hasLatestVisualFacts && !latestMentionsPaperClue
      ? "do not reuse older paper-clue imagery; the current visual focus must be the latest cabinet, object, surface, light, or trace mentioned"
      : "if the dialogue mentions a paper clue, water mark, door gap, security camera, freight elevator, floor drain, key, shelf, box, phone light, or cold vapor, that object must be visibly present",
    absentClues.size > 0
      ? "do not show absent or negated clue objects; if Lin Xia says a possible clue is missing, leave that spot empty and focus on the remaining visible trace"
      : "",
    "the old scene reference is only continuity; the latest dialogue decides the focal clue, camera position, props, and lighting"
  ].filter(Boolean);

  return makeDirectorVisualPrompt(
    dialogueDirectives.join(". "),
    [
      "reinterpret impossible or off-setting player requests as phone-call distractions; keep the image grounded in the current supermarket location",
      "show the physical clue Lin Xia just mentioned, with changed props, camera position, or lighting when the dialogue changed"
    ].join(", ")
  );
}

export function buildConversationVisualTitle(input: ConversationImagePromptInput) {
  const assistantText = removeUnexecutedBranchTerms(input.assistantText);
  const groundedPlayerText = getGroundedPlayerVisualText(input.assistantText, input.playerText);
  const normalizedAssistantText = normalizeSpeechCueText(assistantText);
  const normalizedGroundedPlayerText = normalizeSpeechCueText(groundedPlayerText);
  const absentClues = mergeSets(inferAbsentVisualClues(input.assistantText), inferAbsentVisualClues(groundedPlayerText));
  const deprioritizedClues = inferDeprioritizedVisualClues(groundedPlayerText);
  const assistantCueText = removeExitedLocationTerms(removeDeprioritizedCueTerms(removeAvoidedCueTerms(assistantText), deprioritizedClues));
  const playerCueText = removeExitedLocationTerms(removeDeprioritizedCueTerms(removeAvoidedCueTerms(groundedPlayerText), deprioritizedClues));
  const presentAssistantCueText = removeAbsentCueTerms(assistantCueText, absentClues);
  const presentPlayerCueText = removeAbsentCueTerms(playerCueText, absentClues);
  const text = `${assistantCueText} ${playerCueText}`;
  const presentText = `${presentAssistantCueText} ${presentPlayerCueText}`;
  const cabinetCueText = /(柜内|柜子里|柜里|柜侧|柜边|柜门|柜|cabinet)/i.test(presentAssistantCueText)
    ? presentAssistantCueText
    : `${presentAssistantCueText} ${presentPlayerCueText}`;
  if (/(钥匙|key)/i.test(normalizedAssistantText) && !mentionsAbsentKey(normalizedAssistantText)) {
    return "地漏旁的钥匙";
  }
  if (
    /(脚印|鞋印|shoeprint|footprint)/i.test(normalizedAssistantText) &&
    /(朝|往|延伸|leading|toward).{0,10}(冷库|冷柜|cold storage)/i.test(normalizedAssistantText) &&
    !mentionsAbsentShoeprintCue(normalizedAssistantText)
  ) {
    return "冷库方向脚印";
  }
  if (
    /(后门外|门外|外面|垃圾桶|outside|trash|garbage)/i.test(normalizedAssistantText) &&
    /(脚印|鞋印|shoeprint|footprint)/i.test(normalizedAssistantText) &&
    !mentionsAbsentShoeprintCue(normalizedAssistantText)
  ) {
    return "后门外脚印";
  }
  if (absentClues.has("water") && /(后门|左边|左侧|地上|地面|干净|干燥|back door|left side|floor|dry)/i.test(assistantCueText)) {
    return "后门干燥地面";
  }
  if (absentClues.has("water") && /(冷库|冷柜|门缝|cold storage|door gap)/i.test(assistantCueText)) {
    return "冷库门缝";
  }
  if (
    !absentClues.has("water") &&
    /(水痕|水渍|水印|湿|water|wet)/i.test(normalizedGroundedPlayerText) &&
    /(后门|左边|左侧|back door|left side)/i.test(normalizedGroundedPlayerText) &&
    /(后门|左边|左侧|地上|地面|back door|left side|floor)/i.test(normalizedAssistantText) &&
    /(刚留下|留下的|浅浅|看起来是|fresh|recent)/i.test(normalizedAssistantText)
  ) {
    return "后门地面水痕";
  }
  if (mentionsColdStorageThresholdWater(presentText)) {
    return "冷库门缝水痕";
  }
  if (
    /(脚印|鞋印|shoeprint|footprint)/i.test(presentAssistantCueText) &&
    /(朝|往|延伸|leading|toward).{0,10}(冷库|冷柜|cold storage)/i.test(presentAssistantCueText) &&
    !absentClues.has("shoeprint")
  ) {
    return "冷库方向脚印";
  }
  if (
    mentionsBackDoorOutsideCue(presentAssistantCueText) &&
    /(脚印|鞋印|shoeprint|footprint)/i.test(presentAssistantCueText) &&
    !absentClues.has("shoeprint")
  ) {
    return "后门外脚印";
  }
  if (
    mentionsBackDoorOutsideCue(text) &&
    /(脚印|鞋印|shoeprint|footprint)/i.test(presentAssistantCueText) &&
    !absentClues.has("shoeprint")
  ) {
    return "后门外脚印";
  }
  if (mentionsBackDoorOutsideCue(presentAssistantCueText) && /(垃圾桶|trash|garbage)/i.test(presentAssistantCueText)) {
    return "后门外垃圾桶";
  }
  if (mentionsBackDoorOutsideCue(presentAssistantCueText) && /(水痕|水渍|湿|water|wet)/i.test(presentAssistantCueText)) {
    return "后门外水痕";
  }
  if (mentionsBackDoorGroundTrace(presentAssistantCueText)) {
    return "后门地面水痕";
  }
  if (
    /(货梯|电梯|freight elevator)/i.test(normalizedGroundedPlayerText) &&
    /(金属|摩擦|声音|轻响|嗡嗡|安静|没听到|sound|metal|hum|quiet)/i.test(`${normalizedAssistantText} ${normalizedGroundedPlayerText}`)
  ) {
    return "货梯方向";
  }
  if (/(货梯|电梯|freight elevator)/i.test(normalizedAssistantText) && /(金属|摩擦|声音|轻响|嗡嗡|安静|没听到|sound|metal|hum|quiet)/i.test(normalizedAssistantText)) {
    return "货梯方向";
  }
  if (
    /(地漏|floor drain)/i.test(presentAssistantCueText) &&
    /(水痕|水渍|湿|water|wet)/i.test(presentAssistantCueText) &&
    absentClues.has("key") &&
    /如果没有|直接说没有|不要脑补|别脑补|不用找|别找|don't imagine|do not invent/i.test(input.playerText)
  ) {
    return "地漏水痕";
  }
  if (
    ((/(地漏|floor drain)/i.test(presentText) && /(冷库|冷柜|门缝|cold storage|door gap)/i.test(presentText)) ||
      ((/(冷库|冷柜|cold storage)/i.test(presentText) || input.title === "冷库门缝") && /(门缝|door gap)/i.test(presentText))) &&
    /(水痕|水渍|湿|water|wet)/i.test(presentText)
  ) {
    return "冷库门缝水痕";
  }
  if (/(冷库|冷柜|cold storage)/i.test(presentAssistantCueText) && /(黑影|影子|shadow)/i.test(presentAssistantCueText)) {
    return "冷库门缝黑影";
  }
  if (mentionsFreightElevatorDoorGap(assistantCueText) && /(亮|light|光|微弱|晃动|影子|走动|动|shadow|movement)/i.test(assistantCueText)) {
    return "货梯门缝";
  }
  if (/(货梯|电梯|freight elevator)/i.test(assistantCueText) && /(金属|摩擦|声音|轻响|嗡嗡|安静|没听到|sound|metal|hum|quiet)/i.test(assistantCueText)) {
    return "货梯方向";
  }
  if (/(门缝|door gap)/i.test(assistantCueText) && /(影子|走动|晃动|移动|shadow|movement)/i.test(assistantCueText)) {
    return "门缝暗影";
  }
  if (/(门缝|door gap)/i.test(assistantCueText) && /(光|亮|微弱|light|glow|flicker)/i.test(assistantCueText)) {
    return "门缝微光";
  }
  if (/(门缝|door gap)/i.test(assistantCueText) && /(黑|暗|没透出光|看不清|dark|black|no light)/i.test(assistantCueText)) {
    return "门缝黑暗";
  }
  if (
    /(后门外|门外|外面|service yard|outside)/i.test(presentAssistantCueText) &&
    /(脚步|动静|声音|影子|来电|footstep|sound|shadow|incoming call)/i.test(presentAssistantCueText) &&
    !absentClues.has("movement")
  ) {
    return "后门外动静";
  }
  if (/(冷库|冷柜|cold storage)/i.test(presentAssistantCueText) && /(黑影|影子|shadow)/i.test(presentAssistantCueText)) {
    return "冷库门缝黑影";
  }
  if (/(黑袋子|黑色袋子|塑料袋|bag)/i.test(presentAssistantCueText) && !mentionsAbsentBagCue(assistantCueText)) {
    return "黑袋子";
  }
  if (/(货架|箱子|纸箱|shelf|shelves|box)/i.test(assistantCueText) && /(拖痕|拖过|拖动|drag mark|dragged)/i.test(assistantCueText)) {
    return "货架底部拖痕";
  }
  if (
    /(货架|箱子|纸箱|shelf|shelves|box)/i.test(assistantCueText) &&
    /(底下|下面|下方|底部|under|underneath|bottom)/i.test(assistantCueText) &&
    /(空空|什么都没有|没看到|没有|空的|empty)/i.test(assistantCueText)
  ) {
    return "货架底部空处";
  }
  if (
    /(地漏|floor drain)/i.test(presentAssistantCueText) &&
    /(水痕|水渍|湿|water|wet)/i.test(presentAssistantCueText) &&
    absentClues.has("key") &&
    /如果没有|直接说没有|不要脑补|别脑补|不用找|别找|don't imagine|do not invent/i.test(input.playerText)
  ) {
    return "地漏水痕";
  }
  if (
    ((/(地漏|floor drain)/i.test(presentText) && /(冷库|冷柜|门缝|cold storage|door gap)/i.test(presentText)) ||
      ((/(冷库|冷柜|cold storage)/i.test(presentText) || input.title === "冷库门缝") && /(门缝|door gap)/i.test(presentText))) &&
    /(水痕|水渍|湿|water|wet)/i.test(presentText)
  ) {
    return "冷库门缝水痕";
  }
  if (/(货架|shelf|shelves)/i.test(presentAssistantCueText) && /(水痕|水渍|湿|凉|滴|water|wet|drip)/i.test(presentAssistantCueText)) {
    return "货架底部水痕";
  }
  if (/(货梯|电梯|freight elevator)/i.test(assistantCueText) && /(金属|摩擦|声音|轻响|嗡嗡|安静|没听到|sound|metal|hum|quiet)/i.test(assistantCueText)) {
    return "货梯方向";
  }
  if (/(地漏|floor drain)/i.test(presentAssistantCueText) && /(水痕|水渍|湿|water|wet)/i.test(presentAssistantCueText)) {
    return absentClues.has("key") ? "地漏水痕" : "地漏旁的钥匙";
  }
  if (
    mentionsBackDoorOutsideCue(text) &&
    /(脚印|鞋印|shoeprint|footprint)/i.test(text) &&
    !absentClues.has("shoeprint")
  ) {
    return "后门外脚印";
  }
  if (mentionsBackDoorOutsideCue(text) && /(垃圾桶|trash|garbage)/i.test(text)) {
    return "后门外垃圾桶";
  }
  if (mentionsBackDoorOutsideCue(text) && /(水痕|水渍|湿|water|wet)/i.test(text)) {
    return "后门外水痕";
  }
  if (
    mentionsBackDoorOutsideCue(text) &&
    /(脚步|动静|声音|影子|来电|footstep|sound|shadow|incoming call)/i.test(text) &&
    !absentClues.has("movement")
  ) {
    return "后门外动静";
  }
  if (mentionsBackDoorGroundTrace(presentAssistantCueText)) {
    return "后门地面水痕";
  }
  if (mentionsFreightElevatorDoorGap(assistantCueText) && /(亮|light|光|微弱|晃动|影子|走动|动|shadow|movement)/i.test(assistantCueText)) {
    return "货梯门缝";
  }
  if (/(门缝|door gap)/i.test(assistantCueText) && /(影子|走动|晃动|移动|shadow|movement)/i.test(assistantCueText)) {
    return "门缝暗影";
  }
  if (/(门缝|door gap)/i.test(assistantCueText) && /(光|亮|微弱|light|glow|flicker)/i.test(assistantCueText)) {
    return "门缝微光";
  }
  if (/(门缝|door gap)/i.test(assistantCueText) && /(黑|暗|没透出光|看不清|dark|black|no light)/i.test(assistantCueText)) {
    return "门缝黑暗";
  }
  if (mentionsBackDoorOutsideCue(text)) return "后门外空地";
  if (/(柜内|柜子里|柜里|柜侧|柜边|柜门|柜|cabinet)/i.test(cabinetCueText)) {
    if (/(塑料扣|卡扣|扣子|plastic buckle|clip)/i.test(cabinetCueText) && !mentionsAbsentPlasticCue(cabinetCueText)) {
      return "柜内塑料扣";
    }
    if (/(贴纸|标签|sticker|label)/i.test(cabinetCueText) && !mentionsAbsentStickerCue(cabinetCueText)) return "柜内贴纸";
    if (/(裂缝|细缝|细裂|缝隙|crack|seam)/i.test(cabinetCueText) && !mentionsAbsentCrackCue(cabinetCueText)) return "柜侧细缝";
    if (/(水渍|水痕|刮痕|划痕|scratch|stain|反光|reflection|glint|light)/i.test(cabinetCueText)) return "柜侧痕迹";
  }
  if (/(玻璃|反射|倒影|glass|reflection)/i.test(assistantCueText)) return "货架玻璃";
  if (/(天花板|反光点|ceiling|moving reflection)/i.test(assistantCueText)) return "天花板反光";
  if (/(柜内|柜子里|柜里|柜侧|柜边|柜门|柜|cabinet)/i.test(assistantCueText)) {
    if (/(塑料扣|卡扣|扣子|plastic buckle|clip)/i.test(assistantCueText) && !mentionsAbsentPlasticCue(assistantCueText)) {
      return "柜内塑料扣";
    }
    if (/(贴纸|标签|sticker|label)/i.test(assistantCueText) && !mentionsAbsentStickerCue(assistantCueText)) return "柜内贴纸";
    if (/(裂缝|细缝|细裂|缝隙|crack|seam)/i.test(assistantCueText) && !mentionsAbsentCrackCue(assistantCueText)) return "柜侧细缝";
    if (/(水渍|水痕|刮痕|划痕|scratch|stain)/i.test(assistantCueText)) return "柜侧痕迹";
  }
  if (/(黑袋子|黑色袋子|塑料袋|bag)/i.test(assistantCueText)) return "黑袋子";
  if ((/(地漏|floor drain)/i.test(assistantCueText) && mentionsPresentKey(assistantCueText)) || mentionsPresentKey(assistantCueText)) return "地漏旁的钥匙";
  if (/(地漏|floor drain)/i.test(assistantCueText) && absentClues.has("key")) return "地漏旁空处";
  if (/(冷库|冷柜|门缝|freezer|cold storage|door gap)/i.test(assistantCueText)) return "冷库门缝";
  if (/(监控|摄像头|死角|红点|security camera|blind spot|red indicator)/i.test(assistantCueText)) return "监控死角";
  if (/(货梯|电梯|freight elevator)/i.test(assistantCueText)) return "货梯异响";
  if (mentionsPresentPaperClue(assistantCueText)) return "未结账的小票";
  return input.title;
}

export function buildVisualAwareChoicePrompt(input: VisualAwareChoicePromptInput) {
  const visualTitle = input.visualTitle?.trim();
  const storyTitle = input.storyTitle.trim();
  if (!visualTitle || !storyTitle || visualTitle === storyTitle) return input.basePrompt;
  if (/(空处|空的|没有|未见|缺失|missing|empty)/i.test(visualTitle)) {
    return `下一句可确认${visualTitle}；别急着补不存在的线索。`;
  }

  if (/再推进一步|打开新的线索/.test(input.basePrompt)) {
    return `下一句可顺着${visualTitle}压近；新的线索快露出来了。`;
  }

  if (/落在|确认|处理/.test(input.basePrompt)) {
    return `下一句可顺着${visualTitle}压近；也能让林夏继续确认${storyTitle}。`;
  }

  return `下一句可顺着${visualTitle}压近；也能让林夏继续确认${storyTitle}。`;
}

export function buildUnifiedTurnPlan(input: StoryDirectorPlanInput): UnifiedTurnPlan {
  const director = buildStoryDirectorPlan(input);
  const continuation = director.continuation ?? buildUnifiedFallbackContinuation(input);
  const continuationScriptedText = continuation ? buildPlannedLinXiaLine(input, "continuation") : "";
  const responseScriptedText = buildSceneBeatLinXiaLine(
    input,
    director.response.scriptedText || buildPlannedLinXiaLine(input, "response"),
    continuationScriptedText
  );
  const response = {
    ...director.response,
    scriptedText: responseScriptedText,
    instructions: buildExactLinXiaSpeechInstructions(responseScriptedText, director.response.instructions)
  };
  const unifiedContinuation = continuation
    ? {
        ...continuation,
        scriptedText: continuationScriptedText,
        instructions: buildExactLinXiaSpeechInstructions(continuationScriptedText, continuation.instructions)
      }
    : undefined;
  const visualTarget = buildTurnVisualTarget(input, responseScriptedText, continuationScriptedText);
  const narrations = buildVisualTargetNarrations(input, visualTarget);
  const imageBeats = buildTurnPlanImageBeats(input, visualTarget);
  const visualBeats = director.visualBeats;
  const timeline = buildDirectorTimeline({
    input,
    imageBeats,
    narrations,
    response,
    visualBeats
  });

  return {
    ...director,
    narrations,
    response,
    continuation: unifiedContinuation,
    imageBeats,
    timeline,
    visualTarget,
    inputRelease: "after_sequence"
  };
}

export function buildGuidedOpeningPlayerCue(value: string) {
  const greeting = normalizeSpeechCueText(value);
  const safeGreeting = greeting.replace(/[“”"'\n\r]/g, "").slice(0, 18);
  const prefix = safeGreeting ? `玩家刚接通电话说“${safeGreeting}”。` : "玩家刚接通电话。";
  return `${prefix}林夏不要停在寒暄，也不要等待玩家给下一步指令；她需要主动把手机光扫过超市后门吸烟点的地面水痕、墙边冷气和冷库门缝，直接给出第一个可见现场细节。`;
}

function buildVisualTargetNarrations(
  input: StoryDirectorPlanInput,
  visualTarget: StoryVisualTarget
): Array<StoryNarrationCue & { presentation: StoryNarrationPresentation }> {
  const title = visualTarget.title.trim() || input.title;
  const text = buildVisualTargetNarrationText(input, title);
  return [
    {
      title: "现场",
      text,
      durationMs: input.advanced ? 3400 : 2800,
      presentation: "caption"
    }
  ];
}

function buildVisualTargetNarrationText(input: StoryDirectorPlanInput, title: string) {
  const target = formatBeatTitleForDirector(title);
  if (/(柜侧|柜内|柜子|细缝|裂缝|反光|塑料扣|贴纸|痕迹)/.test(target)) {
    return `手机光压到黄箱下沿，林夏把视线停在${target}，没有再回头看旧线索。`;
  }
  if (/(水痕|水渍|湿|地面|地漏|钥匙)/.test(target)) {
    return `她把手机光贴近地面，光线顺着${target}慢慢扫过去。`;
  }
  if (/(冷库|门缝|蓝白光|脚印)/.test(target)) {
    return `冷气贴着地面涌出来，她把镜头和脚步都停在${target}前。`;
  }
  if (/(监控|摄像头|死角|红点)/.test(target)) {
    return `红点还亮着，她没有走进光里，只把视线卡在${target}边缘。`;
  }
  if (/(货梯|电梯|金属|声音|门缝)/.test(target)) {
    return `货梯方向安静了一瞬，电话里只剩${target}附近的低频电流声。`;
  }
  if (/(货架|纸箱|黑袋子|呼吸|拖痕|空处)/.test(target)) {
    return `她把手机光压低，沿着${target}边缘多停了两秒。`;
  }
  const hint = summarizePlayerText(input.playerText);
  if (hint) return `林夏按你刚才说的方向重新扫过现场，视线落在${target}。`;
  return `林夏没有急着把话交回来，只把现场重新对准${target}。`;
}

function buildTurnVisualTarget(input: StoryDirectorPlanInput, responseScriptedText: string, continuationScriptedText: string): StoryVisualTarget {
  const assistantText = [responseScriptedText, continuationScriptedText].filter(Boolean).join("。");
  const title = buildConversationVisualTitle({
    assistantText,
    playerText: input.playerText,
    title: input.title
  });
  const prompt = `latest player turn is the gameplay trigger for this director timeline. ${buildConversationImagePrompt({
    assistantText,
    playerText: input.playerText,
    title: input.title
  })}`;
  return {
    title,
    prompt,
    referenceMode: input.advanced ? "character" : "both",
    sourceSummary: summarizeVisualSource(`${assistantText} ${input.playerText}`)
  };
}

function buildDirectorTimeline(input: {
  input: StoryDirectorPlanInput;
  imageBeats: StoryImageBeat[];
  narrations: Array<StoryNarrationCue & { presentation: StoryNarrationPresentation }>;
  response: StoryDirectorPlan["response"];
  visualBeats: StoryVisualBeat[];
}): StoryDirectorTimelineEvent[] {
  const cameraBeat = input.visualBeats.find((beat): beat is Extract<StoryVisualBeat, { type: "camera" }> => beat.type === "camera");
  const laterCameraBeat = input.visualBeats
    .filter((beat): beat is Extract<StoryVisualBeat, { type: "camera" }> => beat.type === "camera")
    .find((beat) => beat !== cameraBeat);
  const narration = input.narrations[0];
  const events: StoryDirectorTimelineEvent[] = [
    {
      type: "feedback",
      delayMs: 0,
      label: "已听到",
      text: buildTurnHeardFeedback(input.input),
      mood: "heard"
    }
  ];

  if (cameraBeat) events.push({ type: "camera", delayMs: Math.max(160, cameraBeat.delayMs), beat: cameraBeat });
  const [firstImageBeat, ...laterImageBeats] = input.imageBeats;
  if (firstImageBeat) events.push({ type: "image", delayMs: firstImageBeat.delayMs, beat: firstImageBeat });
  if (narration) events.push({ type: "narration", delayMs: 520, cue: narration });
  events.push({ type: "response", delayMs: input.response.delayMs, response: input.response });
  for (const beat of laterImageBeats) events.push({ type: "image", delayMs: beat.delayMs, beat });
  if (laterCameraBeat) events.push({ type: "camera", delayMs: Math.max(laterCameraBeat.delayMs, 3600), beat: laterCameraBeat });

  return events;
}

function buildTurnHeardFeedback(input: StoryDirectorPlanInput) {
  const hint = summarizePlayerText(input.playerText);
  if (hint) return `林夏听到了“${hint}”，正在把现场对准这个方向。`;
  if (input.advanced) return `林夏准备转向${formatBeatTitleForDirector(input.title)}。`;
  return "林夏听到了，先拿稳电话，把现场重新扫一遍。";
}

export function buildTurnPlanImagePrompt(input: StoryDirectorPlanInput, phase: "setup" | "detail" = "setup") {
  const latestPlayerTurn = normalizeSpeechCueText(input.playerText);
  const latestConfirmedContext = normalizeSpeechCueText(input.priorAssistantText || "");
  const beatPrompt = getStoryBeatPrompt(input.title);
  const phaseDirection =
    phase === "detail"
      ? "second visual beat: push closer to the newest physical clue, change camera distance or angle from the setup image"
      : "first visual beat: establish Lin Xia reacting to the latest player instruction in the current physical location";
  const playerDirective = latestPlayerTurn
    ? `latest player turn, use only the grounded physical part as the current action trigger: ${sanitizeVisualSourceForImagePrompt(latestPlayerTurn)}`
    : "latest player turn has no physical clue, keep Lin Xia grounded in the current supermarket investigation";
  const explicitPlayerCue = /(脚印|鞋印|footprint|shoeprint)/i.test(latestPlayerTurn)
    ? "latest player turn explicitly asks about footprints; if confirmed by the scene, show fresh damp footprints as the focal clue"
    : "";
  const continuityDirective = latestConfirmedContext
    ? `latest confirmed Lin Xia context, preserve only if still compatible: ${sanitizeVisualSourceForImagePrompt(latestConfirmedContext).slice(0, 180)}`
    : "no prior confirmed context for this turn";

  return makeDirectorVisualPrompt(
    [
      "highest priority: this image belongs to the latest player turn, not an older chapter illustration",
      playerDirective,
      explicitPlayerCue,
      continuityDirective,
      `current story beat continuity: ${beatPrompt}`,
      phaseDirection
    ].join(". "),
    [
      "show one grounded scene that visually matches the current turn's action, object, lighting, and camera focus",
      "do not keep drawing an old receipt, key, sticker, cabinet, or paper unless the latest player turn or Lin Xia context still makes it the current focal clue"
    ].join(", ")
  );
}

function buildTurnPlanImageBeats(input: StoryDirectorPlanInput, visualTarget: StoryVisualTarget): StoryImageBeat[] {
  const beats: StoryImageBeat[] = [
    {
      type: "image",
      delayMs: 260,
      title: visualTarget.title,
      prompt: visualTarget.prompt,
      referenceMode: visualTarget.referenceMode,
      reason: "latest_player_turn"
    }
  ];

  if (input.advanced || input.turnsInBeat === 1) {
    beats.push({
      type: "image",
      delayMs: input.advanced ? 2300 : 3200,
      title: visualTarget.title,
      prompt: makeDirectorVisualPrompt(
        visualTarget.prompt,
        "second image beat for the same turn: keep the exact same current clue and location, but move closer to the tactile detail or light reflection"
      ),
      referenceMode: visualTarget.referenceMode,
      reason: input.advanced ? "new_story_location_detail" : "same_turn_detail"
    });
  }

  return beats;
}

function buildUnifiedFallbackContinuation(input: StoryDirectorPlanInput): StoryDirectorPlan["continuation"] {
  const remainingTurns = Math.max(1, input.turnsRequired - input.turnsInBeat);
  if (input.turnsInBeat !== 1 || remainingTurns <= 1) return undefined;
  return {
    delayMs: 850,
    lockReason: "林夏还在继续确认",
    narration: {
      title: "静场",
      text: "她没有马上把话交还给你，只是把手机压近一点，让现场的声音多停了两秒。",
      durationMs: 2600
    },
    instructions:
      `接着上一段，用电话口语主动补充。${LIN_XIA_DIALOGUE_RULE}这一段只说1-2句，总字数45字以内。只能补充当前现场事实，不要写成旁白，不要问玩家下一步。`
  };
}

function buildSceneBeatLinXiaLine(input: StoryDirectorPlanInput, baseLine: string, continuationLine: string) {
  const playerText = normalizeSpeechCueText(input.playerText);
  const normalizedBase = ensureSpokenSentence(baseLine);
  if (!normalizedBase || isSmallTalkOnly(playerText)) return normalizedBase;

  const sentenceCount = normalizedBase.split(/[。！？.!]/u).filter(Boolean).length;
  if (normalizedBase.length >= 70 && sentenceCount >= 2) return normalizedBase;

  const detail = buildLinXiaSceneBeatFollowThrough(input, continuationLine);
  if (!detail) return normalizedBase;
  return `${normalizedBase}${ensureSpokenSentence(detail)}`;
}

function ensureSpokenSentence(value: string) {
  const text = value.replace(/\s+/g, " ").trim();
  if (!text) return "";
  return /[。！？.!]$/.test(text) ? text : `${text}。`;
}

function buildLinXiaSceneBeatFollowThrough(input: StoryDirectorPlanInput, continuationLine: string) {
  const text = normalizeSpeechCueText(`${input.playerText} ${continuationLine}`);
  const holdSafe = /别过去|不要过去|先别|后退|退后|躲|别碰|不要碰|别接|别出声/.test(text);
  if (/(监控|摄像头|红点|死角|blind spot|camera)/i.test(text)) {
    return "红点没有灭，但镜头下沿被箱角挡住一块，柜侧那片正好在它照不到的位置。";
  }
  if (/(脚印|鞋印|footprint|shoeprint)/i.test(text)) {
    return "脚印边缘还湿，每一步都往冷库那条蓝白光靠近，我现在只照地面，不往里面踩。";
  }
  if (/(水痕|水渍|水印|湿|地面|手机光|照|water|wet)/i.test(text)) {
    return `${holdSafe ? "我先停在门边，" : ""}边缘还在反光，黄箱下面那段比别处湿，靠近冷库门缝的位置更亮。`;
  }
  if (/(柜侧|柜内|柜子|周转箱|黄箱|裂缝|细缝|缝隙|反光|cabinet|crack|seam|glint|reflection)/i.test(text)) {
    return "那条缝不是纸边，冷光从里面漏出来一点，旁边还有两粒水珠，我没去碰。";
  }
  if (/(冷库|冷柜|门缝|冷气|蓝白光|freezer|cold storage)/i.test(text)) {
    return "门缝里出来的是蓝白冷光，地面那块水印还没干，我不进去，先卡在黄箱这边看。";
  }
  if (/(地漏|钥匙|key|floor drain)/i.test(text)) {
    return "地漏边缘有新刮痕，水没有完全盖住那个反光点，我先不捡，只确认它卡在边上。";
  }
  if (/(货梯|货运电梯|电梯|金属|声音|门缝|摩擦声|elevator|freight)/i.test(text)) {
    return "我没有往货梯走，声音停住以后，灯管电流声还在，金属门那边没有第二下。";
  }
  if (/(货架|箱子|纸箱|呼吸|黑袋子|袋子|shelf|box|bag|breath)/i.test(text)) {
    return "最底下的纸箱被拖开一点，后面不是墙面，是一块暗的空处，我先站在外侧看。";
  }
  return "我把手机光压低一点，先确认离你刚才说的位置最近的东西，现在能看清的是地面反光和箱子边缘。";
}

function buildExactLinXiaSpeechInstructions(scriptedText: string, guardInstructions: string) {
  return `${guardInstructions}
本轮林夏台词已经由统一导演计划决定。请只逐字说出以下台词：“${scriptedText}”
禁止添加、删改、解释、补问、复述玩家原话，也不要输出旁白或动作描写。`;
}

function buildPlannedLinXiaLine(input: StoryDirectorPlanInput, phase: "response" | "continuation") {
  const text = normalizeSpeechCueText(input.playerText);
  const title = formatBeatTitleForDirector(input.title);
  const holdSafe = /别过去|不要过去|先别|后退|退后|躲|别碰|不要碰|别接|别出声/.test(text);
  const safePrefix = holdSafe ? "我先不往前。" : "";

  if (isNoiseFollowUpQuestion(text, input.priorAssistantText || "")) {
    return phase === "continuation"
      ? "我又听了一下，货梯那边没有第二声，只剩灯管很轻的电流声。"
      : "刚才那一下像货梯方向的轻金属响，现在停了。我还站在后门这边，没有靠过去。";
  }

  if (isFreightListeningRequest(text)) {
    return phase === "continuation"
      ? "我还站在门边听着。货运电梯那边没有第二下，只有灯管的电流声。"
      : buildFreightListeningExactLine(text) || "我站在门边听着。现在没听到金属摩擦声，货运电梯那边是静的。";
  }

  if (/(监控|摄像头|红点|死角|blind spot|camera)/i.test(text)) {
    return phase === "continuation"
      ? "我没碰柜子。红点闪了一下又亮回去，镜头下面那块正好照不到柜侧。"
      : `${safePrefix}我抬头看了，监控红点还亮着，柜侧那片刚好在死角里。`;
  }

  if (/(脚印|鞋印|footprint|shoeprint)/i.test(text)) {
    return phase === "continuation"
      ? "脚印边缘还湿，朝冷库门缝那边断了一截，不像是很久以前留下的。"
      : `${safePrefix}手机光照过去了，地上有一串浅脚印，方向是往冷库门缝那边。`;
  }

  if (/(柜侧|柜内|柜子|周转箱|黄箱|裂缝|细缝|缝隙|反光|cabinet|crack|seam|glint|reflection)/i.test(text)) {
    return phase === "continuation"
      ? "裂缝下沿有水珠，反光不是纸片，是缝里透出来的一点冷光。"
      : `${safePrefix}我蹲下看柜侧最下面，边缘有一条细裂，里面反了一下白光。`;
  }

  if (/(水痕|水渍|水印|湿|地面|手机光|照|water|wet)/i.test(text)) {
    return phase === "continuation"
      ? "水痕边缘很新，黄箱旁那块地反光比刚才重，像是刚拖过去。"
      : `${safePrefix}我用手机光贴着地面扫了一下，水痕是从冷库方向拖到后门缝的。`;
  }

  if (/(冷库|冷柜|门缝|冷气|蓝白光|freezer|cold storage)/i.test(text)) {
    return phase === "continuation"
      ? "门缝里的冷气贴着地面出来，蓝白光下面有一块水印没干。"
      : `${safePrefix}我守在门边看，冷库门缝开着一条，蓝白光一直漏到黄箱边。`;
  }

  if (/(地漏|钥匙|key|floor drain)/i.test(text)) {
    return phase === "continuation"
      ? "地漏边有黑灰和一道新刮痕，钥匙不在水里，是卡在边缘。"
      : `${safePrefix}我低头看了，地漏旁边有个小金属反光，像一把钥匙。`;
  }

  if (/(货架|箱子|纸箱|呼吸|黑袋子|袋子|shelf|box|bag|breath)/i.test(text)) {
    return phase === "continuation"
      ? "纸箱后面没有人出来，但最底下那只箱子被往外拖过一点。"
      : `${safePrefix}我看向货架底下了，纸箱边有拖痕，后面像是空了一块。`;
  }

  if (isSmallTalkOnly(text)) {
    return phase === "continuation"
      ? "后门这边灯还亮着，我先把电话拿稳，别让声音断掉。"
      : "早。后门这边冷得很，我刚抽完烟，电话还在手里。";
  }

  return phase === "continuation"
    ? `我还在${title}这边，刚才那处细节没变，但旁边多了一点新反光。`
    : `${safePrefix}我先按你说的看${title}，这边能确认的是灯光和地面痕迹都对不上。`;
}

export function getNextStoryStep(currentIndex: number): StoryStep {
  const nextIndex = currentIndex + 1;
  const beat = STORY_BEATS[nextIndex];
  if (beat) return { ...beat, index: nextIndex, isExtension: false };

  return {
    title: STORY_EXTENSION_TITLE,
    index: Math.max(STORY_BEATS.length, nextIndex),
    isExtension: true,
    prompt: STORY_EXTENSION_PROMPT,
    videoUrl: "",
    videoPrompt: "",
    videoDurationSeconds: 5,
    videoHasAudio: false
  };
}

export function getNextStoryStepForPlayerIntent(currentIndex: number, playerText: string) {
  const intendedStep = getExplicitStoryStepForPlayerIntent(currentIndex, playerText);
  if (intendedStep) return intendedStep;

  return getNextStoryStep(currentIndex);
}

export function getStoryStepForPlayerFocus(playerText: string) {
  const intendedIndex = inferStoryBeatIndexesFromPlayerText(playerText, { includeSoftWaterCue: false }).find((index) =>
    isAllowedImmediateStoryFocus(index, playerText)
  );
  if (intendedIndex === undefined) return undefined;
  const intendedBeat = STORY_BEATS[intendedIndex];
  if (!intendedBeat) return undefined;
  return { ...intendedBeat, index: intendedIndex, isExtension: false };
}

export function getExplicitStoryStepForPlayerIntent(currentIndex: number, playerText: string, options: StoryAdvanceOptions = {}) {
  const maxForward = options.maxForward ?? 2;
  const intendedIndex = inferStoryBeatIndexesFromPlayerText(playerText).find((index) =>
    isAllowedImmediateStoryFocus(index, playerText) && isPlayableStoryAdvance(currentIndex, index, playerText, maxForward)
  );
  if (intendedIndex === undefined) return undefined;
  const intendedBeat = STORY_BEATS[intendedIndex];
  if (!intendedBeat) return undefined;
  return { ...intendedBeat, index: intendedIndex, isExtension: false };
}

function isPlayableStoryAdvance(currentIndex: number, intendedIndex: number, playerText: string, maxForward: number) {
  if (intendedIndex <= currentIndex) return false;
  if (intendedIndex <= currentIndex + maxForward) return true;
  return (
    intendedIndex === 5 &&
    currentIndex >= 2 &&
    currentIndex <= 4 &&
    intendedIndex <= currentIndex + 3 &&
    /(地漏|钥匙|floor\s*drain|key)/i.test(playerText)
  );
}

export function buildStoryDirectorPlan(input: StoryDirectorPlanInput): StoryDirectorPlan {
  const title = formatBeatTitleForDirector(input.title);
  const playerHint = summarizePlayerText(input.playerText);
  const depriorityRule = buildDeprioritizedDialogueRule(input.playerText);
  const priorFactRule = buildPriorAssistantFactRule(input.priorAssistantText);
  const freightExactLine = buildFreightListeningExactLine(input.playerText);
  const freightListeningRule = buildFreightListeningRule(input.playerText);
  const freightExactRule = freightExactLine
    ? `本轮是货运电梯听声结果，不要自由发挥。只允许逐字说这一句：“${freightExactLine}”禁止添加其他句子，禁止改写，禁止追加动作建议。`
    : "";
  const directObservationRule =
    "如果玩家让你确认“有没有、是不是、往哪边、听一下、看一下”，必须先给出明确观察结果，并在同一段说清；不能只说“我看看”“我摸摸”“我听听”“等我确认”，禁止说“等下告诉你”“待会再看”“待会告诉你”“有新发现我再告诉你”“如果有新发现我再告诉你”“有的话告诉你”，禁止任何“如果/要是/有声音/有发现 + 告诉你”的延后交代；至少说出一个有/没有、听到/没听到、看到的形状、方向、位置或触感；如果确认存在某个线索，必须直接说出线索名，比如水痕、脚印、钥匙、红点、金属摩擦声、胶带、刮痕、塑料片、贴纸，不要只说“刚留下的”“是新留下的”“看起来不太对”；不要把确认动作反问回玩家。";
  const boundedActionRule =
    "如果玩家限定“只看、只听、不开门、别出去、先别碰”，你必须留在这个边界内，结尾也不要建议改去听、开门、出去或触碰。";
  const antiHandoffRule =
    "不要把下一步选择说进林夏的台词里；整段禁止出现“你想”，整段禁止出现“想让我”，整段禁止出现“你要”，整段禁止出现“你要我”，整段禁止出现“要不要”，整段禁止出现“还是去”，整段禁止出现“接下来呢”，整段禁止出现“怎么办”，整段禁止出现“你那边”，整段禁止出现“还有别的”，整段禁止出现“你觉得”，整段禁止出现“下一步”，整段禁止出现“吗”，整段禁止出现“呢”，整段禁止出现问号；禁止用“你想...”收尾，禁止用“想让我...”收尾，禁止用“要不要...”收尾，禁止用“还是去...”收尾，禁止用“下一步怎么说...”收尾，禁止用“接下来呢...”收尾；最后一个字符不能是“吗”“呢”或问号。需要停顿时只停在已经确认的现场事实。";
  if (input.advanced) {
    return {
      lockReason: `林夏正转向${title}`,
      narrations: [
        {
          title: "旁白",
          text: `电话里短暂安静了一下。林夏没有立刻催你，她像是把你刚才的话在脑子里重新摆了一遍。`,
          durationMs: 4200
        },
        {
          title: "镜头",
          text: `画面跟着她的视线转向${title}。${playerHint ? `你提到的“${playerHint}”让这个线索显得更不对劲。` : "后门的冷光被拉长，新的细节慢慢浮出来。"}`,
          durationMs: 5200
        }
      ],
      visualBeats: buildAdvancedVisualBeats(title),
      response: {
        delayMs: 1500,
        scriptedText: freightExactLine || undefined,
        instructions:
          `你是林夏，用中文电话口语说话。${LIN_XIA_DIALOGUE_RULE}${depriorityRule}${priorFactRule}${freightExactRule}${freightListeningRule}${directObservationRule}${boundedActionRule}${antiHandoffRule}不要写成旁白，不要写小说、动作描写或镜头语言，不要复述玩家原话。直接对玩家说你在「${title}」这边确认到的异常，用“我这边”“等下”“先别急”这类自然短句。如果玩家刚才明确要求安全动作，比如躲到门边、后退、别接电话，先执行并汇报看到的现场事实，不要反问要不要执行。不要马上把问题抛给玩家；最后一句自然表示你还没结束，但不要使用固定尾句。`
      },
      continuation: {
        delayMs: 900,
        lockReason: "林夏还在确认新的线索",
        narration: {
          title: "停顿",
          text: `她没有挂断，也没有等你立刻回答。手机那头只剩细小的电流声，像是她在把现场重新对齐。`,
          durationMs: 3000
        },
        instructions:
          `接着上一段，用电话口语主动补充。${LIN_XIA_DIALOGUE_RULE}${depriorityRule}${priorFactRule}${freightExactRule}${freightListeningRule}${directObservationRule}${boundedActionRule}${antiHandoffRule}这一段只说1-2句，总字数45字以内。必须承接上一段，补充而不是推翻；不得改口或否认上一段刚说过的事实，不要重复上一段已经说过的完整句子或同一组关键词。上一段说不存在的东西，后面不得用“捡起来”“拿起来”“收起来”去指代它；玩家明确禁止的动作，不要在结尾反问是否执行这个被禁止动作；玩家明确要求的安全动作要直接执行并汇报，不要反问要不要执行。不要写成旁白，不要写小说、动作描写或镜头语言，不要假装玩家又说了新话。基于刚才的「${title}」说一个更具体的异常，最后停在仍在确认的状态，不要问玩家下一步。`
      },
      transition: {
        title: input.title,
        detail: `镜头转向${title}，场景图会在生成完成后切换。`
      },
      choicePrompt: `下一句可落在${title}；让林夏继续确认这条线索。`
    };
  }

  const remainingTurns = Math.max(1, input.turnsRequired - input.turnsInBeat);
  const ambientText =
    input.turnsInBeat % 2 === 1
      ? `林夏把手机压在肩侧，先照你说的方向确认一遍。后门缝里灌出的冷气贴着地面爬过去，黄箱边缘被灯光照得发白。`
      : `她停了两秒，没有急着让你继续说。货架和周转箱之间传来很轻的塑料摩擦声，像是有什么刚被碰过。`;
  const shouldContinue = input.turnsInBeat === 1 && remainingTurns > 1 && !asksForImmediateObservation(input.playerText);
  const isImmediateObservation = asksForImmediateObservation(input.playerText);
  const finalResponseRule = isImmediateObservation
    ? "如果玩家刚才明确要求安全动作，比如躲到门边、后退、别接电话，先执行并汇报看到的现场事实，不要反问要不要执行。最后停在当前观察结果，不要提出下一步动作，不要提出可尝试动作。"
    : "如果玩家刚才明确要求安全动作，比如躲到门边、后退、别接电话，先执行并汇报看到的现场事实，不要反问要不要执行。最后只给一个很轻的下一步提示。";
  const responseFocus = playerHint
    ? `根据玩家刚才提醒的「${playerHint}」，直接说你这边发现了什么现场细节。`
    : `围绕「${title}」这个位置，直接说你这边发现了什么现场细节。`;

  return {
    lockReason: "林夏正在观察现场",
    narrations: [
      {
        title: "旁白",
        text: ambientText,
        durationMs: 4700
      },
      {
        title: "现场",
        text:
          remainingTurns <= 1
            ? `这条线索快要收束了。林夏的语气低下来，像是准备把视线挪向下一个更危险的位置。`
            : `她没有离开后门，只是把观察范围慢慢扩大，让这通电话多留出一点呼吸。`,
        durationMs: 4300
      }
    ],
    visualBeats: buildAmbientVisualBeats(title, input.turnsInBeat, remainingTurns),
    response: {
      delayMs: 1100,
      scriptedText: freightExactLine || undefined,
      instructions:
        `这轮不要像问答一样立刻追问玩家。你是林夏，用中文电话口语说话。${LIN_XIA_DIALOGUE_RULE}${depriorityRule}${priorFactRule}${freightExactRule}${freightListeningRule}${directObservationRule}${boundedActionRule}${antiHandoffRule}不要写成旁白，不要写小说、动作描写或镜头语言，不要复述玩家原话。${responseFocus}${
          shouldContinue
            ? "如果玩家刚才明确要求安全动作，比如躲到门边、后退、别接电话，先执行并汇报看到的现场事实，不要反问要不要执行。结尾不要提问，不要把话交还给玩家；最后一句自然表示你还没结束，但不要使用固定尾句。"
            : finalResponseRule
        }`
    },
    continuation: shouldContinue
      ? {
          delayMs: 850,
          lockReason: "林夏还在继续看",
          narration: {
            title: "静场",
            text:
              remainingTurns <= 1
                ? `她把呼吸压低，手机没有离开耳边。这个位置像是快要露出下一条线索。`
                : `她又多看了一眼，没有急着让你开口。冷气和灯声把几秒钟拉得更长。`,
            durationMs: 2800
          },
          instructions:
            `接着上一段，用电话口语主动继续。${LIN_XIA_DIALOGUE_RULE}${depriorityRule}${priorFactRule}${freightExactRule}${freightListeningRule}${directObservationRule}${boundedActionRule}${antiHandoffRule}这一段只说1-2句，总字数45字以内。必须承接上一段，补充而不是推翻；不得改口或否认上一段刚说过的事实，不要重复上一段已经说过的完整句子或同一组关键词。上一段说不存在的东西，后面不得用“捡起来”“拿起来”“收起来”去指代它；玩家明确禁止的动作，不要在结尾反问是否执行这个被禁止动作；玩家明确要求的安全动作要直接执行并汇报，不要反问要不要执行。不要写成旁白，不要写小说、动作描写或镜头语言，不要假装玩家又说了新话。补一个更细的现场发现，最后停在仍在确认的状态，不要问玩家下一步。`
        }
      : undefined,
    choicePrompt: remainingTurns <= 1 ? "下一句再压近一点，新的线索快露出来了。" : "下一句可顺着现场细节压近；让林夏继续确认当前位置。"
  };
}

function formatBeatTitleForDirector(title: string) {
  return title.replace(/^超市/, "");
}

function summarizePlayerText(value: string) {
  const compact = trimCuePunctuation(
    removeDeprioritizedCueTerms(removeDialogueMetaTerms(removeAvoidedCueTerms(normalizeSpeechCueText(value))), inferDeprioritizedVisualClues(value))
      .replace(/\s+/g, "")
      .replace(/[“”"']/g, "")
  );
  if (isSmallTalkOnly(compact) || isOffSettingRequest(compact)) return "";

  return compact.length > 24 ? `${compact.slice(0, 24)}…` : compact;
}

function trimCuePunctuation(value: string) {
  return value.replace(/^[，,、。！？；：:;.!?\s]+|[，,、。！？；：:;.!?\s]+$/g, "");
}

function buildDeprioritizedDialogueRule(value: string) {
  const labels = [...inferDeprioritizedVisualClues(value)].map(formatDeprioritizedCueLabel).filter(Boolean);
  if (labels.length === 0) return "";
  return `本轮玩家明确降权或排除的线索：${labels.join("、")}。不要复述这些词，不要说“我没动它们”“没把它们当线索”，不要把它们作为发现或结尾选项；把话集中到仍然存在的细缝、水痕、光线、声音、影子等现场事实。`;
}

function buildPriorAssistantFactRule(value?: string) {
  const prior = normalizeDialogueForPrompt(value || "").slice(0, 120);
  if (!prior) return "";
  return `上一轮林夏刚确认过：${prior}。本轮必须沿用这个事实，不得把有改成没有，不得把没听到改成听到，不得把“好像有/不太确定”直接改成相反事实；可以说“刚才那一下停了”或“我还不确定”，但必须说明变化。玩家条件句只有在上一轮事实满足条件时才执行，否则先说条件不成立，再继续围绕已确认的位置观察；条件不成立时禁止描述条件分支里的物体、位置、脚印、垃圾桶或结果。如果玩家说“如果地面没有线索”“如果没有线索”“如果没看到”但上一轮已经确认有水痕、湿痕、拖痕、钥匙、红点、声音或其他异常，必须先说条件不成立，不要转向、靠近或执行条件分支。`;
}

function buildFreightListeningRule(value: string) {
  const text = normalizeSpeechCueText(value);
  if (!isFreightListeningRequest(text)) return "";
  return "货梯是 freight elevator。如果玩家让你听货梯、听金属摩擦声或要求别靠太近，只能原地听并报告听到/没听到；必须当场回答现在听到或没听到，并且台词里必须包含“听到”“没听到”“没有声音”或“有金属摩擦声”之一，不要把结果推迟到以后；不要提脚部动作、敲门动作、触碰或接触测试动作，不要提出动作建议；禁止任何“如果/要是/有声音/有发现 + 告诉你”的延后交代，禁止说“有的话告诉你”，禁止说“有声音我告诉你”，禁止说“如果有声音我马上告诉你”，禁止说“等下告诉你”，禁止建议靠近或测试它。";
}

function buildFreightListeningExactLine(value: string) {
  const text = normalizeSpeechCueText(value);
  if (!isFreightListeningRequest(text)) return "";
  if (/第二次|再次|继续|再听|还听|又/i.test(text)) {
    return "我站在门边继续听。没听到第二次摩擦声，货运电梯那边现在是静的。";
  }
  return "我站在门边听着。现在没听到金属摩擦声，货运电梯那边是静的。";
}

function isFreightListeningRequest(text: string) {
  return /(货梯|电梯|freight elevator|金属摩擦|摩擦声)/i.test(text);
}

function formatDeprioritizedCueLabel(value: string) {
  switch (value) {
    case "paper":
      return "小票";
    case "sticker":
      return "贴纸";
    case "plastic":
      return "塑料扣";
    case "key":
      return "钥匙";
    default:
      return "";
  }
}

function mentionsFreightElevatorDoorGap(value: string) {
  return /(?:货梯|电梯|freight elevator)[^。！？；;]{0,12}(?:门缝|door gap)|(?:门缝|door gap)[^。！？；;]{0,12}(?:货梯|电梯|freight elevator)/i.test(
    value
  );
}

function mentionsBackDoorGroundTrace(value: string) {
  if (!/(水痕|水渍|湿|拖痕|鞋印|water|wet|shoeprint)/i.test(value)) return false;
  if (/(货架|地漏|冷库|冷柜|柜侧|柜内|货梯|shelf|floor drain|cold storage|cabinet|freight elevator)/i.test(value)) return false;
  return /(后门|左边|左侧|地板|地面|left side)/i.test(value);
}

function mentionsColdStorageThresholdWater(value: string) {
  if (!/(水痕|水渍|湿|water|wet)/i.test(value)) return false;
  return /(冷库|冷柜|cold storage)/i.test(value) && /(门缝|door gap)/i.test(value);
}

function mentionsBackDoorOutsideCue(value: string) {
  if (/(后门外|门外|service yard|outside the supermarket back door|outside the back door|垃圾桶|trash|garbage|来电|incoming call|雨|rain)/i.test(value)) {
    return true;
  }
  return /(外面|outside)/i.test(value) && !/(冷库|冷柜|门缝|freezer|cold storage|door gap)/i.test(value);
}

function removeDialogueMetaTerms(value: string) {
  return value
    .replace(/如果(?:她|你|林夏)?(?:开始|有点|很)?(?:紧张|害怕|慌)[，,、]*/g, "")
    .replace(/(?:低声|小声)?告诉我(?:她|你|林夏)?(?:看见了什么|看到什么|发现什么)/g, "看见了什么")
    .replace(/别紧张|不要紧张|先冷静/g, "")
    .trim();
}

export function isSmallTalkOnly(value: string) {
  const normalized = value
    .toLowerCase()
    .replace(/[，。！？、,.!?;；:：~～…—\-（）()【】\[\]《》<>]/g, "");

  return /^(早上好|早安|上午好|中午好|下午好|晚上好|晚安|你好|您好|哈喽|哈罗|喂|在吗|hello|hi|hey|嗯+|啊+|哦+|哈+|呵+|嘿+|笑死|好|好的|好吧|行|行吧|可以|ok|okay)$/.test(
    normalized
  );
}

export function shouldHoldStoryAdvanceForPlayerTurn(playerText: string, priorAssistantText = "") {
  const text = normalizeSpeechCueText(playerText);
  if (!text || isSmallTalkOnly(text)) return true;
  return isClarifyingFollowUpQuestion(text, priorAssistantText);
}

function isClarifyingFollowUpQuestion(text: string, priorAssistantText = "") {
  if (isActionableObservationRequest(text)) return false;
  if (isNoiseFollowUpQuestion(text, priorAssistantText)) return true;
  return /(什么|啥|哪|哪里|哪边|哪个|刚才|你说|意思|为什么|怎么回事|什么情况)/.test(text) && /[？?]?$/.test(text);
}

function isActionableObservationRequest(text: string) {
  return /(先|去|看|听|照|扫|查|确认|靠近|摸|打开|转向|回头|找|盯|蹲|抬头)/.test(text) && hasGroundedVisualCue(text);
}

function isNoiseFollowUpQuestion(text: string, priorAssistantText = "") {
  const combined = `${text} ${normalizeSpeechCueText(priorAssistantText)}`;
  if (!/(异响|声音|动静|响|金属|摩擦|货梯|电梯)/.test(combined)) return false;
  return /(?:什么|啥|哪|哪里|哪边|哪个|刚才|你说|怎么回事|为什么)[^。！？；;]{0,10}(?:异响|声音|动静|响)|(?:异响|声音|动静|响)[^。！？；;]{0,10}(?:什么|啥|哪|哪里|哪边|哪个|刚才|你说|怎么回事|为什么)/.test(
    text
  );
}

function inferDialogueVisualIntent(input: ConversationImagePromptInput) {
  const deprioritizedClues = inferDeprioritizedVisualClues(input.playerText);
  const assistantText = removeExitedLocationTerms(removeDeprioritizedCueTerms(normalizeSpeechCueText(input.assistantText), deprioritizedClues));
  const playerText = isOffSettingRequest(input.playerText)
    ? ""
    : removeExitedLocationTerms(removeDeprioritizedCueTerms(normalizeSpeechCueText(input.playerText), deprioritizedClues));
  const text = `${removeAvoidedCueTerms(assistantText)} ${removeAvoidedCueTerms(playerText)}`;
  const absentClues = mergeSets(inferAbsentVisualClues(assistantText), inferAbsentVisualClues(playerText));
  const presentText = removeAbsentCueTerms(text, absentClues);
  const presentAssistantText = removeAbsentCueTerms(removeAvoidedCueTerms(assistantText), absentClues);
  const cues: string[] = [];

  if (mentionsPresentPaperClue(text)) {
    cues.push("crumpled unpaid receipt as the main clue");
  }
  if (
    /(脚印|鞋印|shoeprint|footprint)/i.test(presentText) &&
    /(朝|往|延伸|leading|toward).{0,10}(冷库|冷柜|cold storage)/i.test(presentText) &&
    !absentClues.has("shoeprint")
  ) {
    cues.push("fresh damp footprints leading from the back-door concrete toward the cold storage threshold");
  }
  if (
    mentionsBackDoorOutsideCue(presentAssistantText) &&
    /(脚印|鞋印|shoeprint|footprint)/i.test(presentAssistantText) &&
    !absentClues.has("shoeprint")
  ) {
    cues.push("outside the supermarket back door, broken damp footprints near a trash can, cold service yard shadows");
  }
  if (
    (mentionsBackDoorOutsideCue(presentText) || /巷子|alley/i.test(presentText)) &&
    /(脚印|鞋印|shoeprint|footprint)/i.test(presentAssistantText) &&
    !absentClues.has("shoeprint")
  ) {
    cues.push("outside the supermarket back door, damp footprints continuing into the narrow service alley");
  }
  if (/(黑袋子|黑色袋子|塑料袋|袋口|bag)/i.test(presentAssistantText) && !mentionsAbsentBagCue(assistantText)) {
    cues.push("black plastic bag under the stockroom shelf as the main focal clue, drag marks beside it");
  }
  if (!absentClues.has("water") && /(水泡|水印|水痕|水渍|湿痕|湿印|water marks?|wet marks?|wet streaks?|damp marks?|smeared)/i.test(presentText)) {
    cues.push("wet paper, smeared ink, damp marks on concrete");
  }
  if (/(凌晨三点|三点|3点|3\s*am|3am)/i.test(text)) {
    cues.push("suspicious 3 AM timestamp or indicator");
  }
  if (/(冷库|冷柜|冷气|冷风|门缝|蓝白光|freezer|cold storage|door gap)/i.test(text)) {
    cues.push("half-open cold storage door gap, blue-white freezer light, cold vapor crawling along the floor");
  }
  if (/(监控|摄像头|死角|红点|blind spot|security camera)/i.test(text)) {
    cues.push("old security camera, blind corner, red indicator light, partially covered camera view");
  }
  if (/(货梯|电梯|指示灯|金属|freight elevator)/i.test(text)) {
    cues.push("freight elevator doors, dim indicator light, industrial metal shadows");
  }
  if (/(地漏|floor drain)/i.test(presentText) && (/(水痕|水渍|湿|water|wet)/i.test(presentText) || absentClues.has("key"))) {
    cues.push("floor drain and cold storage threshold with shallow water marks on the concrete");
  }
  if (mentionsPresentKey(text) && !absentClues.has("key")) {
    cues.push("small key near a floor drain, scuff marks, black dust, interrupted wet footprints");
  }
  if (/(柜侧|柜内|柜门|柜子|柜|cabinet)/i.test(text)) {
    cues.push("metal cabinet side or cabinet interior as the main focal area");
  }
  if (/(裂缝|细缝|缝隙|crack|seam)/i.test(text)) {
    cues.push("new crack or narrow seam on the cabinet surface");
  }
  if (/(贴纸|标签|起翘|sticker|label|peeling)/i.test(text) && !absentClues.has("sticker")) {
    cues.push("peeling sticker edge or torn label on the cabinet");
  }
  if (/(塑料扣|卡扣|扣子|plastic buckle|clip)/i.test(text) && !absentClues.has("plastic")) {
    cues.push("broken plastic buckle or small snapped plastic clip");
  }
  if (/(底板|层板|隔板|panel|shelf board)/i.test(text)) {
    cues.push("bottom panel or shelf panel with fresh scratch marks");
  }
  if (/(刮痕|划痕|scratch|scuff)/i.test(text)) {
    cues.push("fresh scratch marks on the surface");
  }
  if (/(黑袋子|黑色袋子|塑料袋|袋口|bag)/i.test(text) && !absentClues.has("bag")) {
    cues.push("black plastic bag near the crates or shelves, bag opening visible");
  }
  if (/(红绳|绳子|扎着|绑着|cord|rope)/i.test(text)) {
    cues.push("red cord or red rope tied around the object");
  }
  if (/(透明胶带|胶带|封箱带|tape)/i.test(text)) {
    cues.push("torn transparent tape catching the light");
  }
  if (/(货架|纸箱|呼吸|箱子|shelves|boxes|breathing)/i.test(text)) {
    cues.push("dim stockroom shelves, shifted cardboard boxes, tense empty aisle");
  }
  if (/(后门|门外|雨|来电|电话|service yard|incoming call)/i.test(text)) {
    cues.push("supermarket back door, concrete service yard, phone glow, rain mist or cold vapor");
  }
  if (mentionsBackDoorOutsideCue(text) && /(垃圾桶|trash|garbage)/i.test(text)) {
    cues.push("outside the supermarket back door, trash can beside clean wet concrete, quiet empty service yard shadows");
  }
  if (
    mentionsBackDoorOutsideCue(text) &&
    /(脚印|鞋印|shoeprint|footprint)/i.test(text) &&
    !absentClues.has("shoeprint")
  ) {
    cues.push("outside the supermarket back door, broken damp footprints near a trash can, cold service yard shadows");
  }
  if (
    mentionsBackDoorOutsideCue(text) &&
    /(脚步|动静|声音|影子|footstep|sound|shadow|movement)/i.test(text) &&
    !absentClues.has("movement")
  ) {
    cues.push("outside the supermarket back door, cold service yard shadows with a subtle distant sign of movement");
  }
  if (/(手电|手机|照|光|phone|light)/i.test(text)) {
    cues.push("phone light or flashlight beam revealing the clue");
  }

  if (cues.length === 0) cues.push(`${getStoryBeatImageCue(input.title)} physical clue inside the late-night supermarket back area`);

  return dedupeStrings(cues).join(", ");
}

function summarizeVisualSource(value: string, absentClues = new Set<string>(), deprioritizedClues = new Set<string>()) {
  const compact = removeExitedLocationTerms(removeDeprioritizedCueTerms(removeAvoidedCueTerms(normalizeDialogueForPrompt(value)), deprioritizedClues));
  if (!compact) return "";

  const fragments = splitDialogueFragments(compact)
    .filter((fragment) => !isSmallTalkOnly(fragment) && !isOffSettingRequest(fragment))
    .filter((fragment) => !isNegatedOnlyVisualFragment(fragment))
    .filter((fragment) => hasGroundedVisualCue(fragment) || hasConcreteVisualCue(fragment))
    .slice(0, 3);
  const source = fragments.length > 0 ? fragments.join("; ") : compact;
  return removeNegatedVisualResidue(sanitizeVisualSourceForImagePrompt(removeAbsentCueTerms(source, absentClues))).slice(0, 180);
}

function summarizeGroundedPlayerVisualHint(value: string, absentClues = new Set<string>(), deprioritizedClues = new Set<string>()) {
  const compact = removeExitedLocationTerms(removeDeprioritizedCueTerms(removeAvoidedCueTerms(normalizeDialogueForPrompt(value)), deprioritizedClues));
  if (!compact || isSmallTalkOnly(compact) || isOffSettingRequest(compact)) return "";

  const fragments = splitDialogueFragments(compact)
    .filter((fragment) => hasGroundedVisualCue(fragment) || hasConcreteVisualCue(fragment))
    .slice(0, 2);
  const source = fragments.length > 0 ? removeAbsentCueTerms(fragments.join("; "), absentClues) : "";
  return removeNegatedVisualResidue(sanitizeVisualSourceForImagePrompt(source)).slice(0, 120);
}

function getGroundedPlayerVisualText(assistantText: string, playerText: string) {
  if (isUnconfirmedConditionalPlayerBranch(assistantText, playerText)) return "";
  return removeUnconfirmedPlayerCueTerms(assistantText, playerText);
}

function removeUnconfirmedPlayerCueTerms(assistantText: string, playerText: string) {
  const assistant = normalizeSpeechCueText(assistantText);
  let next = playerText;

  const impliesPlayerWaterTrace = /(刚留下|留下的|浅浅|痕迹|一道|一条|那道|那条|这道|这条|fresh|recent|trace)/i.test(assistant);
  if (!impliesPlayerWaterTrace && !/(水痕|水渍|水印|湿痕|湿印|潮湿|湿|water|wet|damp)/i.test(assistant)) {
    next = next
      .replace(/(?:沿着|顺着)?(?:地上|地面|那道|那条|这道|这条|一条|一道|浅浅的)?(?:水痕|水渍|水印|湿痕|湿印|潮湿|湿|water marks?|wet marks?|wet streaks?|damp marks?)(?:往|朝|看|去|走|延伸)?/gi, "看")
      .replace(/(?:水痕|水渍|水印|湿痕|湿印|潮湿|湿|water marks?|wet marks?|wet streaks?|damp marks?)/gi, "");
  }

  return next.replace(/看{2,}/g, "看").replace(/\s+/g, " ").trim();
}

function isUnconfirmedConditionalPlayerBranch(assistantText: string, playerText: string) {
  const normalizedPlayer = normalizeDialogueForPrompt(playerText);
  if (!/(如果|假如|要是|if\b)/i.test(normalizedPlayer)) return false;
  if (!/(回头|转头|出去|后门外|门外|外面|垃圾桶|脚印|鞋印|outside|trash|garbage|shoeprints?|footprints?)/i.test(normalizedPlayer)) {
    return false;
  }

  const normalizedAssistant = normalizeDialogueForPrompt(assistantText);
  if (/条件不成立|condition failed/i.test(normalizedAssistant)) return true;
  const branchNotTaken = /(没回头|没有回头|不回头|没出去|没有出去|先不出去|没去|没有去|没离开|没有离开|不往外|留在|还在)/i.test(
    normalizedAssistant
  );
  const conditionFailed =
    /(?:里面|里边|门里)[^。！？；;]{0,8}有[^。！？；;]{0,8}(?:声音|响动|异响|动静|摩擦声)|(?<!没)(?:听到|听见)[^。！？；;]{0,8}(?:声音|响动|异响|动静|摩擦声)/i.test(
      normalizedAssistant
    );
  return branchNotTaken || conditionFailed;
}

function normalizeDialogueForPrompt(value: string) {
  return value
    .replace(/[“”"']/g, "")
    .replace(/\s+/g, " ")
    .replace(/[^\p{L}\p{N}\s，。！？、,.!?;；:：-]/gu, " ")
    .trim();
}

function splitDialogueFragments(value: string) {
  return value
    .split(/[。！？!?；;]+/g)
    .map((fragment) => fragment.trim())
    .filter(Boolean);
}

function hasGroundedVisualCue(value: string) {
  return /(超市|后门|门外|外面|员工|休息点|周转箱|黄箱|纸箱|货架|柜侧|柜内|柜门|柜子|冷库|冷柜|门缝|冷气|冷风|小票|票据|收据|未结账|时间|凌晨|三点|水印|水痕|潮|湿|监控|摄像头|死角|红点|货梯|电梯|指示灯|地漏|钥匙|塑料扣|卡扣|底板|层板|隔板|刮痕|划痕|裂缝|贴纸|标签|脚印|脚步|垃圾桶|手机|手电|光|雨|雾|墙|地面|concrete|cabinet|receipt|freezer|camera|elevator|drain|key|shelf|box|phone|light|water|wet|outside|trash|garbage|footstep)/i.test(
    value
  );
}

function hasConcreteVisualCue(value: string) {
  return /(左边|右边|旁边|上面|下面|背后|角落|柜|柜侧|柜内|底板|层板|隔板|黑|白|红|黄|蓝|绿|袋子|塑料袋|纸袋|垃圾桶|脚步|绳|线|胶带|贴纸|标签|塑料扣|卡扣|锁|链子|手套|布|衣角|影子|裂缝|污渍|划痕|刮痕|痕迹|bag|cabinet|trash|garbage|footstep|rope|cord|tape|sticker|label|lock|chain|glove|shadow|stain|scratch|crack|buckle|clip)/i.test(
    value
  );
}

function isOffSettingRequest(value: string) {
  return /(火星|月球|宇宙|太空|高达|机甲|魔法|龙|古代|城堡|海边|沙漠|学校|医院|办公室|卧室|窗帘|飞|穿越|mars|moon|space|spaceship|mecha|gundam|dragon|castle|bedroom|curtain)/i.test(
    value
  );
}

function inferAbsentVisualClues(value: string) {
  const absent = new Set<string>();
  if (mentionsAbsentKey(value)) absent.add("key");
  if (mentionsAbsentPlasticCue(value)) absent.add("plastic");
  if (mentionsAbsentStickerCue(value)) absent.add("sticker");
  if (mentionsAbsentCrackCue(value)) absent.add("crack");
  if (mentionsAbsentShoeprintCue(value)) absent.add("shoeprint");
  if (mentionsAbsentWaterTraceCue(value)) absent.add("water");
  if (mentionsAbsentBagCue(value)) absent.add("bag");
  if (mentionsAbsentMovementCue(value)) absent.add("movement");
  return absent;
}

function inferDeprioritizedVisualClues(value: string) {
  const deprioritized = new Set<string>();
  if (mentionsAvoidedPaperClue(value)) deprioritized.add("paper");
  if (mentionsDeprioritizedCue(value, "(?:贴纸|标签|sticker|label)")) deprioritized.add("sticker");
  if (mentionsDeprioritizedCue(value, "(?:塑料扣|卡扣|扣子|plastic buckle|clip)")) deprioritized.add("plastic");
  if (mentionsDeprioritizedCue(value, "(?:钥匙|key)") || mentionsAbsentKey(value)) deprioritized.add("key");
  if (mentionsAbsentBagCue(value)) deprioritized.add("bag");
  return deprioritized;
}

function removeDeprioritizedCueTerms(value: string, deprioritizedClues: Set<string>) {
  const fragments = splitDialogueFragments(value).map((fragment) => {
    let next = fragment;
    if (deprioritizedClues.has("paper")) {
      next = next
        .replace(/(?:柜子?侧面|柜侧|侧面)?那?张?(?:小票|票据|收据|receipt)[^。！？；;，,]{0,18}/gi, "")
        .replace(/(?:小票|票据|收据|receipt)[^。！？；;，,]{0,18}/gi, "");
    }
    if (deprioritizedClues.has("sticker")) {
      next = next
        .replace(/(?:贴纸|标签|sticker|label)[^。！？；;，,]{0,18}/gi, "")
        .replace(/(?:没有|没看到|没看见|没发现|不当|不要当|别当|不是).{0,8}(?:贴纸|标签|sticker|label)/gi, "");
    }
    if (deprioritizedClues.has("plastic")) {
      next = next
        .replace(/(?:塑料扣|卡扣|扣子|plastic buckle|plastic clip|clip)[^。！？；;，,]{0,18}/gi, "")
        .replace(/(?:没有|没看到|没看见|没发现|不当|不要当|别当|不是|不存在).{0,8}(?:塑料扣|卡扣|扣子|plastic buckle|plastic clip|clip)/gi, "");
    }
    if (deprioritizedClues.has("key")) {
      next = next
        .replace(/(?:钥匙|key)[^。！？；;，,]{0,18}/gi, "")
        .replace(/(?:没有|没看到|没看见|没发现|不在|不存在|别找|不用找).{0,8}(?:钥匙|key)/gi, "");
    }
    if (deprioritizedClues.has("bag")) {
      next = next
        .replace(/(?:黑袋子|黑色袋子|塑料袋|black bag|plastic bag)[^。！？；;，,]{0,18}/gi, "")
        .replace(/(?:没有|没看到|没看见|没发现|不当|不要当|别当|不是|不存在).{0,8}(?:黑袋子|黑色袋子|塑料袋|black bag|plastic bag)/gi, "");
    }
    next = next
      .replace(/\s+/g, " ")
      .trim();

    if (!hasGroundedVisualCue(next) && !hasConcreteVisualCue(next)) return "";
    return next;
  });

  return fragments.filter(Boolean).join("。").trim();
}

function removeExitedLocationTerms(value: string) {
  return value
    .replace(
      /(?:我|她|林夏)?(?:现在|已经|先)?(?:离开|离开了|走离|走开|退开|离远)(?:了)?(?:柜侧|柜内|柜门|柜子|柜边|冷库门缝|冷库|货架|货梯|地漏)(?:这边|那边|附近|旁边)?(?:了)?/gi,
      ""
    )
    .replace(
      /(?:从|离开)(?:柜侧|柜内|柜门|柜子|柜边|冷库门缝|冷库|货架|货梯|地漏)(?:这边|那边|附近|旁边)?(?:走开|退开|出来|往外走)?/gi,
      ""
    )
    .replace(/\s+/g, " ")
    .trim();
}

function removeUnexecutedBranchTerms(value: string) {
  return removeContradictedOutsideBranchTerms(value)
    .replace(
      /(?:垃圾桶|后门外|门外|外面|脚印|鞋印|trash\s*can|garbage|shoeprints?|footprints?)[^。！？；;，,]{0,24}(?:待会|等会|等下|之后|稍后|晚点|回头)(?:再)?(?:看|确认|去|处理)/gi,
      ""
    )
    .replace(
      /(?:我|她|林夏)?(?:先)?(?:不|没|没有)(?:回头|转头|出去|去|离开|追|看(?!到|见))[^。！？；;，,]{0,22}(?:垃圾桶|后门外|门外|外面|脚印|鞋印|trash\s*can|garbage|shoeprints?|footprints?)(?:那边|那里|附近)?/gi,
      ""
    )
    .replace(
      /(?:垃圾桶|后门外|门外|外面|脚印|鞋印|trash\s*can|garbage|shoeprints?|footprints?)[^。！？；;，,]{0,18}(?:先)?(?:不看|没看|没有看|没回头|没有回头|不追|没追|没有追)/gi,
      ""
    )
    .replace(/\s+/g, " ")
    .trim();
}

function removeContradictedOutsideBranchTerms(value: string) {
  if (!/(垃圾桶|后门外|门外|外面|脚印|鞋印|trash\s*can|garbage|shoeprints?|footprints?)/i.test(value)) return value;
  if (
    !/(条件不成立|没去看|没去|没有去|没回头|没有回头|不回头|先不出去|没出去|没有出去|等会儿再说|待会(?:儿)?再说|之后再说|稍后再说|里面有[^。！？；;]{0,8}(?:声音|响动|异响|动静)|门缝里有[^。！？；;]{0,8}(?:声音|响动|异响|动静))/i.test(
      value
    )
  ) {
    return value;
  }

  return splitDialogueFragments(value)
    .filter((fragment) => !/(垃圾桶|后门外|门外|外面|脚印|鞋印|trash\s*can|garbage|shoeprints?|footprints?)/i.test(fragment))
    .join("。");
}

function mentionsDeprioritizedCue(value: string, cuePattern: string) {
  const downrank = "(?:不要|别|不用|不必|先别|别再|不是|不算|并非|别把|不要把|不用把|不要当|别当|不当|不是线索|不算线索|障眼法)";
  return new RegExp(`${downrank}[^。！？；;]{0,18}${cuePattern}|${cuePattern}[^。！？；;]{0,18}${downrank}`, "i").test(value);
}

function isNegatedOnlyVisualFragment(value: string) {
  const cleaned = removeAbsentCueTerms(value, inferAbsentVisualClues(value));
  return cleaned.length < value.length && !/(水痕|水渍|湿|潮|拖痕|刮痕|划痕|裂缝|贴纸|标签|塑料扣|卡扣|门缝|冷气|红点|货梯|water|wet|scratch|crack|sticker|label|clip|door gap)/i.test(cleaned);
}

function removeAbsentCueTerms(value: string, absentClues: Set<string>) {
  let next = value;
  if (absentClues.has("key")) {
    next = next
      .replace(/(?:地上|地面|附近|旁边)?(?:有)?(?:没有|没看到|没看见|没发现|没找到|未见|看不到|无)?钥匙(?:或者|或)?/gi, "")
      .replace(/(?:small\s+)?key(?:\s+or)?/gi, "")
      .replace(/或者\s*([，。！？、,.!?;；:：])/g, "$1")
      .replace(/或者/g, "");
  }
  if (absentClues.has("plastic")) {
    next = next
      .replace(/(?:也)?(?:没有|没看到|没看见|没发现|没找到|未见|看不到|不是|不算|并非|无).{0,8}(?:塑料扣|卡扣|扣子)(?:或者|或)?/gi, "")
      .replace(/(?:不存在|不在|没有|无)的?(?:塑料扣|卡扣|扣子)(?:或者|或)?/gi, "")
      .replace(/(?:塑料扣|卡扣|扣子).{0,8}(?:没有|没看到|没看见|没发现|没找到|未见|看不到|不是|不算|并非|不存在|无)(?:或者|或)?/gi, "")
      .replace(/(?:also\s+)?(?:no|not|not\s+seeing|not\s+found|without)\s+(?:a\s+)?(?:plastic\s+)?(?:buckle|clip)/gi, "")
      .replace(/(?:nonexistent|non-existing|absent|missing)\s+(?:plastic\s+)?(?:buckle|clip)/gi, "")
      .replace(/(?:plastic\s+)?(?:buckle|clip)\s+(?:is\s+)?(?:missing|absent|not\s+there)/gi, "");
  }
  if (absentClues.has("sticker")) {
    next = next
      .replace(/(?:也)?(?:没有|没看到|没看见|没发现|没找到|未见|看不到|不是|不算|并非|无).{0,8}(?:贴纸|标签)(?:或者|或)?/gi, "")
      .replace(/(?:不存在|不在|没有|无)的?(?:贴纸|标签)(?:或者|或)?/gi, "")
      .replace(/(?:贴纸|标签).{0,8}(?:没有|没看到|没看见|没发现|没找到|未见|看不到|不是|不算|并非|不存在|无)(?:或者|或)?/gi, "")
      .replace(/(?:also\s+)?(?:no|not|not\s+seeing|not\s+found|without)\s+(?:a\s+)?(?:sticker|label)/gi, "")
      .replace(/(?:nonexistent|non-existing|absent|missing)\s+(?:sticker|label)/gi, "")
      .replace(/(?:sticker|label)\s+(?:is\s+)?(?:missing|absent|not\s+there)/gi, "");
  }
  if (absentClues.has("crack")) {
    next = next
      .replace(/(?:也)?(?:没有|没看到|没看见|没发现|没找到|未见|看不到|不是|不算|并非|无).{0,8}(?:裂缝|细缝|缝隙)(?:或者|或)?/gi, "")
      .replace(/(?:裂缝|细缝|缝隙).{0,8}(?:没有|没看到|没看见|没发现|没找到|未见|看不到|不是|不算|并非|不存在|无)(?:或者|或)?/gi, "")
      .replace(/(?:also\s+)?(?:no|not|not\s+seeing|not\s+found|without)\s+(?:a\s+)?(?:crack|seam)/gi, "")
      .replace(/(?:crack|seam)\s+(?:is\s+)?(?:missing|absent|not\s+there)/gi, "");
  }
  if (absentClues.has("shoeprint")) {
    next = next
      .replace(/(?:也)?(?:没有|没看到|没看见|没发现|没找到|未见|看不到|无).{0,8}(?:脚印|鞋印)(?:或者|或)?/gi, "")
      .replace(/(?:脚印|鞋印).{0,8}(?:没有|没看到|没看见|没发现|没找到|未见|看不到|不存在|无)(?:或者|或)?/gi, "")
      .replace(/(?:also\s+)?(?:no|not|not\s+seeing|not\s+found|without)\s+(?:a\s+)?(?:shoeprints?|footprints?)/gi, "")
      .replace(/(?:shoeprints?|footprints?)\s+(?:are\s+|is\s+)?(?:missing|absent|not\s+there|not\s+visible)/gi, "");
  }
  if (absentClues.has("water")) {
    next = next
      .replace(/(?:也)?(?:没有|没看到|没看见|没发现|没找到|未见|看不到|无).{0,8}(?:水痕|水渍|水印|湿痕|湿印|潮湿|湿)(?:或者|或)?/gi, "")
      .replace(/(?:水痕|水渍|水印|湿痕|湿印|潮湿|湿).{0,8}(?:没有|没看到|没看见|没发现|没找到|未见|看不到|不存在|无)(?:或者|或)?/gi, "")
      .replace(/(?:沿着|顺着)?(?:这些|那些|这道|那道|这条|那条|一条|一道|地上|地面)?(?:水痕|水渍|水印|湿痕|湿印|潮湿|湿)(?:看下去|看|往|朝|去|走|延伸)?(?:吗)?/gi, "")
      .replace(/(?:也)?(?:没(?:有|看到|看见|发现|找到)?|没有|未见|看不到|无)\s*(?:water\s+marks?|wet\s+marks?|wet\s+streaks?|damp\s+marks?)/gi, "")
      .replace(/(?:also\s+)?(?:no|not|not\s+seeing|not\s+found|without)\s+(?:water\s+marks?|wet\s+marks?|wet\s+streaks?|damp\s+marks?)/gi, "")
      .replace(/(?:these|those|the|this|that)?\s*(?:water\s+marks?|wet\s+marks?|wet\s+streaks?|damp\s+marks?)(?:\s+(?:ahead|further|down|along))?/gi, "")
      .replace(/(?:water\s+marks?|wet\s+marks?|wet\s+streaks?|damp\s+marks?)\s+(?:are\s+|is\s+)?(?:missing|absent|not\s+there|not\s+visible)/gi, "");
  }
  if (absentClues.has("bag")) {
    next = next
      .replace(/(?:也)?(?:没有|没看到|没看见|没发现|没找到|未见|看不到|无).{0,8}(?:黑袋子|黑色袋子|塑料袋)(?:或者|或)?/gi, "")
      .replace(/(?:黑袋子|黑色袋子|塑料袋).{0,8}(?:没有|没看到|没看见|没发现|没找到|未见|看不到|不存在|无)(?:或者|或)?/gi, "")
      .replace(/(?:also\s+)?(?:no|not|not\s+seeing|not\s+found|without)\s+(?:a\s+)?(?:black\s+)?(?:plastic\s+)?bag/gi, "")
      .replace(/(?:black\s+)?(?:plastic\s+)?bag\s+(?:is\s+)?(?:missing|absent|not\s+there|not\s+visible)/gi, "");
  }
  if (absentClues.has("movement")) {
    next = next
      .replace(/(?:也)?(?:没有|没听到|没听见|没发现|未听到|听不到|无).{0,8}(?:动静|声音|脚步声?|影子)(?:或者|或)?/gi, "")
      .replace(/(?:也)?(?:没有|没看到|没看见|没发现|未见|看不到|无).{0,8}(?:影子|人影)(?:或者|或)?/gi, "")
      .replace(/(?:动静|声音|脚步声?|影子|人影).{0,8}(?:没有|没听到|没听见|没看到|没看见|没发现|未听到|未见|听不到|看不到|无)(?:或者|或)?/gi, "")
      .replace(/(?:also\s+)?(?:no|not|not\s+hearing|without)\s+(?:footsteps?|sound|movement|shadow)/gi, "")
      .replace(/(?:footsteps?|sound|movement|shadow)\s+(?:are\s+|is\s+)?(?:missing|absent|not\s+there|not\s+audible)/gi, "");
  }
  return next.replace(/\s+/g, " ").trim();
}

function removeAvoidedCueTerms(value: string) {
  return value
    .replace(/(?:先)?别(?:再)?(?:盯着|看|管|碰|拿|检查|找)?[^。！？；;，,]{0,8}(?:小票|票据|收据|receipt)/gi, "")
    .replace(/(?:不要|不用|先别|别再)[^。！？；;，,]{0,10}(?:小票|票据|收据|receipt)/gi, "")
    .replace(/(?:小票|票据|收据|receipt)[^。！？；;，,]{0,12}(?:障眼法|别看|不要看|不用看|先别看|不看|不管|别管)/gi, "")
    .replace(/(?:钥匙|key)[^。！？；;，,]{0,8}(?:不在|不存在|不是重点|别找|不用找)/gi, "")
    .replace(/(?:别|不要|不用)[^。！？；;，,]{0,10}(?:钥匙|key)/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

function mentionsAbsentKey(value: string) {
  const text = normalizeExistenceQuestionText(value)
    .replace(/(?:没|没有|未)(?:标|写|贴)?(?:编号|标签|号码|号牌)/g, "")
    .replace(/(?:没|没有|未)(?:刻|写|标)?(?:字|文字|刻字|铭文|markings?|engraving)/gi, "")
    .replace(/(?:没|没有|未)(?:什么)?(?:特别|明显|可疑)?(?:的)?(?:标记|特征|记号|markings?|marks?|features?)/gi, "");
  return /(没(?:有|看到|看见|发现|找到)?|没有|未见|看不到|无).{0,8}(钥匙|key)|(钥匙|key).{0,8}(没(?:有|看到|看见|发现|找到)?|没有|未见|看不到|不在|不存在|无)/i.test(
    text
  );
}

function mentionsPresentKey(value: string) {
  return /(钥匙|key)/i.test(value) && !mentionsAbsentKey(value);
}

function mentionsAbsentPlasticCue(value: string) {
  return mentionsAbsentCue(value, "(?:塑料扣|卡扣|扣子|plastic buckle|clip)");
}

function mentionsAbsentStickerCue(value: string) {
  return mentionsAbsentCue(value, "(?:贴纸|标签|sticker|label)");
}

function mentionsAbsentCrackCue(value: string) {
  return mentionsAbsentCue(value, "(?:裂缝|细缝|缝隙|crack|seam)");
}

function mentionsAbsentShoeprintCue(value: string) {
  const text = normalizeExistenceQuestionText(value);
  return /(?:没(?:有|看到|看见|发现|找到)?|没有|未见|看不到|无)[^。！？；;]{0,10}(?:脚印|鞋印|shoeprints?|footprints?)|(?:脚印|鞋印|shoeprints?|footprints?)[^。！？；;]{0,10}(?:没有|未见|看不到|不存在|无)/i.test(
    text
  );
}

function mentionsAbsentWaterTraceCue(value: string) {
  const text = normalizeExistenceQuestionText(value);
  return /(?:没(?:有|看到|看见|发现|找到)?|没有|未见|看不到|无)[^。！？；;]{0,10}(?:水痕|水渍|水印|湿痕|湿印|潮湿|湿|water marks?|wet marks?|wet streaks?|damp marks?)|(?:water marks?|wet marks?|wet streaks?|damp marks?)\s+(?:are\s+|is\s+)?(?:missing|absent|not\s+there|not\s+visible)/i.test(
    text
  );
}

function mentionsAbsentBagCue(value: string) {
  const text = normalizeExistenceQuestionText(value);
  return /(?:没(?:有|看到|看见|发现|找到)?|没有|未见|看不到|无)[^。！？；;]{0,10}(?:黑袋子|黑色袋子|塑料袋|black bag|plastic bag)|(?:黑袋子|黑色袋子|塑料袋|black bag|plastic bag)[^。！？；;]{0,10}(?:没有|未见|看不到|不存在|无)/i.test(
    text
  );
}

function mentionsAbsentMovementCue(value: string) {
  const text = normalizeExistenceQuestionText(value);
  return /(?:没(?:有|听到|听见|看到|看见|发现)?|没有|未听到|未见|听不到|看不到|无)[^。！？；;]{0,10}(?:动静|声音|脚步声?|影子|人影|footsteps?|sound|movement|shadow)|(?:动静|声音|脚步声?|影子|人影|footsteps?|sound|movement|shadow)[^。！？；;]{0,10}(?:没有|没看到|没看见|未见|未听到|听不到|看不到|不存在|无)/i.test(
    text
  );
}

function mentionsAbsentCue(value: string, cuePattern: string) {
  const text = normalizeExistenceQuestionText(value);
  const negation = "(?:没(?:有|看到|看见|发现|找到)?|没有|未见|看不到|不存在|不在|不是|不算|并非|无)";
  return new RegExp(`${negation}[^。！？；;]{0,12}${cuePattern}|${cuePattern}[^。！？；;]{0,12}${negation}`, "i").test(text);
}

function normalizeExistenceQuestionText(value: string) {
  return value
    .replace(/如果(?:没|没有|没看到|没看见|没发现|没找到|未见|看不到|无)[^。！？；;，,]{0,20}/g, "")
    .replace(/if\s+(?:there\s+is\s+)?(?:no|not|none|missing)[^.!?;,]{0,32}/gi, "")
    .replace(/有没有/g, "是否有")
    .replace(/有无/g, "是否有")
    .replace(/是否没有/g, "是否缺少");
}

function sanitizeVisualSourceForImagePrompt(value: string) {
  return value
    .replace(/柜侧/g, "cabinet side")
    .replace(/柜内/g, "cabinet interior")
    .replace(/柜门/g, "cabinet door")
    .replace(/柜子/g, "metal cabinet")
    .replace(/新裂缝/g, "new crack")
    .replace(/裂缝/g, "crack")
    .replace(/贴纸边缘起翘/g, "peeling sticker edge")
    .replace(/贴纸/g, "sticker")
    .replace(/标签/g, "label")
    .replace(/起翘/g, "peeling")
    .replace(/断裂塑料扣/g, "broken plastic buckle")
    .replace(/塑料扣/g, "plastic buckle")
    .replace(/卡扣/g, "plastic clip")
    .replace(/底板/g, "bottom panel")
    .replace(/层板/g, "shelf panel")
    .replace(/隔板/g, "shelf divider")
    .replace(/新刮痕/g, "fresh scratch marks")
    .replace(/新划痕/g, "fresh scratch marks")
    .replace(/黑色?塑料袋/g, "black plastic bag")
    .replace(/黑色?袋子/g, "black bag")
    .replace(/袋口/g, "bag opening")
    .replace(/红色?绳子/g, "red rope")
    .replace(/红绳/g, "red cord")
    .replace(/透明胶带/g, "transparent tape")
    .replace(/封箱带/g, "packing tape")
    .replace(/胶带/g, "tape")
    .replace(/周转箱/g, "yellow plastic produce crate")
    .replace(/黄箱/g, "yellow plastic crate")
    .replace(/就在外面听着/g, "at the cold storage threshold listening")
    .replace(/在外面听着/g, "at the cold storage threshold listening")
    .replace(/后门外/g, "outside the supermarket back door")
    .replace(/门外/g, "outside the back door")
    .replace(/外面/g, "outside")
    .replace(/垃圾桶/g, "trash can")
    .replace(/脚步声/g, "footsteps")
    .replace(/脚步/g, "footsteps")
    .replace(/动静/g, "movement sound")
    .replace(/左边/g, "left side")
    .replace(/右边/g, "right side")
    .replace(/背后/g, "behind")
    .replace(/后面/g, "behind")
    .replace(/压在/g, "pressed against")
    .replace(/被撕开/g, "torn open")
    .replace(/撕开/g, "torn open")
    .replace(/小票被水泡过/g, "receipt soaked by water")
    .replace(/未结账那行/g, "the unpaid receipt row")
    .replace(/小票时间/g, "receipt timestamp")
    .replace(/一把小?钥匙/g, "a small key")
    .replace(/黑色刮痕/g, "black scuff marks")
    .replace(/地漏旁边/g, "beside the floor drain")
    .replace(/死角/g, " security camera blind corner ")
    .replace(/红点不亮/g, " red indicator light is off ")
    .replace(/红点/g, " red indicator light ")
    .replace(/镜头/g, " security camera ")
    .replace(/摄像头/g, " security camera ")
    .replace(/监控/g, " security camera ")
    .replace(/遮挡/g, " partially covered ")
    .replace(/挡住/g, " partially covered ")
    .replace(/磨掉/g, " rubbed blank patch ")
    .replace(/刮痕/g, " scratch marks ")
    .replace(/划痕/g, " scratch marks ")
    .replace(/钥匙/g, " small key ")
    .replace(/地漏/g, " floor drain ")
    .replace(/水痕/g, " water marks ")
    .replace(/水渍/g, " water stains ")
    .replace(/拖痕/g, " wet streak marks ")
    .replace(/被拖过的痕迹/g, " wet streak marks ")
    .replace(/拖过的痕迹/g, " wet streak marks ")
    .replace(/脚印/g, " shoeprint-shaped damp mark ")
    .replace(/未结账/g, " unpaid ")
    .replace(/小票/g, " receipt ")
    .replace(/冷库/g, " cold storage ")
    .replace(/门缝/g, " door gap ")
    .replace(/冷气/g, " cold air vapor ")
    .replace(/\s+/g, " ")
    .trim();
}

function removeNegatedVisualResidue(value: string) {
  return value
    .replace(/(?:也)?(?:没(?:有|看到|看见|发现|找到)?|没有|未见|看不到|无)\s*(?:water\s+marks?|wet\s+marks?|wet\s+streaks?|damp\s+marks?)/gi, "")
    .replace(/(?:also\s+)?(?:no|not|not\s+seeing|not\s+found|without)\s+(?:water\s+marks?|wet\s+marks?|wet\s+streaks?|damp\s+marks?)/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

function mentionsPaperClue(value: string) {
  return /(小票|票据|收据|未结账|receipt)/i.test(value);
}

function mentionsPresentPaperClue(value: string) {
  return mentionsPaperClue(value) && !/(?:别|不要|不用|先别|别再|不看|不管|障眼法).{0,12}(?:小票|票据|收据|receipt)|(?:小票|票据|收据|receipt).{0,12}(?:别|不要|不用|先别|别再|不看|不管|障眼法)/i.test(value);
}

function mentionsAvoidedPaperClue(value: string) {
  return /(?:别|不要|不用|先别|别再|不看|不管|障眼法).{0,14}(?:小票|票据|收据|receipt)|(?:小票|票据|收据|receipt).{0,14}(?:别|不要|不用|先别|别再|不看|不管|障眼法)/i.test(
    value
  );
}

function inferLatestLocationCue(text: string, fallbackTitle: string) {
  if (mentionsBackDoorOutsideCue(text)) {
    return "outside or just beyond the supermarket back door, concrete service yard, wet ground, trash can, cold vapor and hard shadows";
  }
  if (/(货梯|电梯|freight elevator)/i.test(text)) {
    return getStoryBeatLocationCue("凌晨三点的货梯");
  }
  if (/(监控|摄像头|死角|红点|security camera|blind spot|red indicator)/i.test(text)) {
    return getStoryBeatLocationCue("监控死角");
  }
  if (/(冷库|冷柜|冷气|冷风|门缝|freezer|cold storage|door gap)/i.test(text)) {
    return getStoryBeatLocationCue("冷库门缝");
  }
  if (/(地漏|floor drain)/i.test(text)) {
    return getStoryBeatLocationCue("地漏旁的钥匙");
  }
  if (/(货架|纸箱|呼吸|箱子|shelves|boxes|breathing)/i.test(text)) {
    return getStoryBeatLocationCue("货架后的呼吸声");
  }
  if (/(柜侧|柜内|柜门|柜子|柜|cabinet)/i.test(text)) {
    return getStoryBeatLocationCue("未结账的小票");
  }
  return getStoryBeatLocationCue(fallbackTitle);
}

function getStoryBeatLocationCue(title: string) {
  switch (title) {
    case "未结账的小票":
      return "near the back-room cabinet, yellow plastic crates, cardboard boxes, damp concrete floor, gray wall, cold fluorescent light";
    case "冷库门缝":
      return "near the cold storage entrance, freezer light, cold vapor, wet concrete and storage shelves";
    case "监控死角":
      return "near the back corridor corner with wall-mounted fixtures, shelves, crates and industrial shadows";
    case "凌晨三点的货梯":
      return "near the freight elevator, metal doors, indicator light, stacked boxes and service corridor";
    case "地漏旁的钥匙":
      return "near the floor drain, wet concrete, scuff marks, crates and low industrial light";
    case "货架后的呼吸声":
      return "inside the dim stockroom aisle, metal shelves, boxes and narrow walkways";
    case "后门外的来电":
      return "under the back door awning, concrete service yard, rain mist, crates and phone glow";
    default:
      return "around the supermarket back entrance staff area, gray concrete, yellow crates, cardboard boxes, metal shelves and cold vapor";
  }
}

function inferStoryBeatIndexFromPlayerText(value: string) {
  return inferStoryBeatIndexesFromPlayerText(value)[0];
}

function inferStoryBeatIndexesFromPlayerText(value: string, options: { includeSoftWaterCue?: boolean } = {}) {
  const normalizedValue = normalizeSpeechCueText(value);
  const rawText = removeAvoidedCueTerms(normalizedValue).replace(/\s+/g, "");
  const text = removeExitedLocationTerms(removeDeprioritizedCueTerms(removeAvoidedCueTerms(normalizedValue), inferDeprioritizedVisualClues(normalizedValue))).replace(/\s+/g, "");
  const explicitIndexes: number[] = [];
  if (/(地漏|floordrain|floor\s*drain)/i.test(rawText) && /(钥匙|key|东西|object)/i.test(rawText)) explicitIndexes.push(5);
  const rules: Array<[RegExp, number]> = [
    [/(后门外|门外|外面|服务院|来电|脚步|垃圾桶|serviceyard|outside|incomingcall|trash|garbage|footstep)/i, 7],
    [/(小票|票据|收据|未结账|收银)/i, 1],
    [/(冷库|冷柜|门缝|冷气|冷风|温控)/i, 2],
    [/(监控|摄像头|死角|红点|遮挡|挡住)/i, 3],
    [/(货梯|电梯|凌晨三点|三点|指示灯)/i, 4],
    [/(钥匙|key|地漏.{0,8}钥匙|floordrain.{0,8}key)/i, 5],
    [/(货架|纸箱|呼吸声|箱子后|仓库)/i, 6],
    [/(雨|服务院)/i, 7]
  ];
  if (options.includeSoftWaterCue !== false) rules.splice(3, 0, [/(水痕|水渍|水印|地面湿|wet|water)/i, 2]);
  return dedupeStrings([...explicitIndexes.map((index) => String(index)), ...rules.filter(([pattern]) => pattern.test(text)).map(([, index]) => String(index))]).map(
    (index) => Number(index)
  );
}

function isAllowedImmediateStoryFocus(intendedIndex: number, playerText: string) {
  if (intendedIndex === 2 && isColdStorageDirectionProbe(playerText)) return false;
  return true;
}

function isColdStorageDirectionProbe(value: string) {
  const text = normalizeSpeechCueText(value).replace(/\s+/g, "");
  if (!/(水痕|水渍|水印|湿|拖痕|脚印|鞋印|water|wet|footprint|shoeprint)/i.test(text)) return false;
  if (!/(冷库|冷柜|coldstorage|freezer)/i.test(text)) return false;
  if (/(转去|去冷库|靠近|走到|走近|到冷库门|到门边|贴到|贴近|推门|开门|打开|进去|进冷库|看门缝|听门缝|照门缝)/i.test(text)) {
    return false;
  }
  return /(通向|通往|通到|连到|是不是.*冷库|是否.*冷库|冷库方向|从冷库|来自冷库|往冷库)/i.test(text);
}

function dedupeStrings(values: string[]) {
  return [...new Set(values)];
}

export function normalizeSpeechCueText(value: string) {
  return value.replace(/[或获活][梯踢]/g, "货梯");
}

function asksForImmediateObservation(value: string) {
  const text = normalizeSpeechCueText(normalizeDialogueForPrompt(value));
  return /(有没有|是否有|有无|是不是|亮不亮|看一下|看看|看一眼|听一下|听听|确认|描述|只说|实际看到|手机照|把灯|抬头看|蹲下看|靠近.{0,8}看|保持.{0,8}听|别靠太近)/i.test(
    text
  );
}

function mergeSets<T>(...sets: Array<Set<T>>) {
  return new Set(sets.flatMap((set) => [...set]));
}

function buildAmbientVisualBeats(
  formattedTitle: string,
  turnsInBeat: number,
  remainingTurns: number
): StoryVisualBeat[] {
  const focus: StoryVisualFocus = remainingTurns <= 1 ? "detail" : turnsInBeat % 2 === 1 ? "medium" : "wide";

  return [
    {
      type: "camera",
      delayMs: 250,
      label: "镜头调整",
      detail: `镜头先贴近${formattedTitle}，让现场继续动起来。`,
      focus,
      mood: remainingTurns <= 1 ? "tense" : "observe"
    },
    {
      type: "camera",
      delayMs: 3600,
      label: "视线扫过",
      detail: remainingTurns <= 1 ? "镜头停在快要暴露下一条线索的位置。" : "镜头扫过周转箱和门缝，等林夏继续说下去。",
      focus: remainingTurns <= 1 ? "detail" : "medium",
      mood: remainingTurns <= 1 ? "reveal" : "tense"
    }
  ];
}

function buildAdvancedVisualBeats(formattedTitle: string): StoryVisualBeat[] {
  return [
    {
      type: "camera",
      delayMs: 250,
      label: "转向新线索",
      detail: `镜头拉开，林夏准备转向${formattedTitle}。`,
      focus: "wide",
      mood: "reveal"
    },
    {
      type: "camera",
      delayMs: 3900,
      label: "细节推进",
      detail: `镜头压到${formattedTitle}的关键细节。`,
      focus: "detail",
      mood: "tense"
    }
  ];
}

function makeDirectorVisualPrompt(basePrompt: string, visualDirection: string) {
  return [
    basePrompt,
    visualDirection,
    "single full-frame scene image, one continuous environment, one camera angle, no comic panels, no manga panels, no collage, no split screen, no storyboard, no inset frames",
    LIN_XIA_IMAGE_STYLE,
    "cinematic late-night supermarket realism, dynamic camera framing, clear focal point"
  ].join(". ");
}

function getStoryBeatImageCue(title: string) {
  if (title === STORY_EXTENSION_TITLE) return "connected extended clue";
  const beat = STORY_BEATS.find((item) => item.title === title);
  const slug = beat?.videoUrl.match(/\/([^/.]+)\.mp4$/)?.[1]?.replace(/^\d+-/, "");
  return slug ? slug.replace(/-/g, " ") : formatBeatTitleForDirector(title);
}
